/**
 * - Overrides the default product hit template with native `<product-card>` markup.
 * - Renders color swatches from Algolia-indexed `available_color_swatches` metafield data.
 * - Styling is handled by `algolia-instantsearch-theme.css`; facet accordion by
 *    `algolia-instantsearch-facet-accordion.js` (loaded from theme.liquid).
 * - Loaded on: collection and search templates only.
 */

const setupFacetClassEnhancer = () => {
  var ROOT_SELECTOR = '.ais-facets';
  var FACET_WRAPPER_SELECTOR = '.ais-facet-dropdown-wrapper';

  const slugifyFacetName = (raw) => {
    if (!raw) return '';

    var decoded = raw;
    try {
      decoded = decodeURIComponent(raw);
    } catch (e) {
      decoded = raw.replace(/%20/g, ' ');
    }

    return decoded
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '-')   // spaces → dashes
      .replace(/[^\w-]/g, ''); // strip weird chars
  }

  const enhanceFacets = (root) => {
    if (!root) return;

    // Root wrapper class
    root.classList.add('teq-facets-root');

    var wrappers = root.querySelectorAll(FACET_WRAPPER_SELECTOR);
    if (!wrappers.length) return;

    Array.prototype.forEach.call(wrappers, function (wrapper, index) {
      // Generic class for all facets
      wrapper.classList.add('teq-facet');

      // ----- 1) Add attribute-based class: teq-facet--price, teq-facet--vendor, etc.
      var facetName = null;
      wrapper.className.split(/\s+/).forEach(function (cls) {
        if (cls.indexOf('ais-facet-') === 0 && cls !== 'ais-facet-dropdown-wrapper') {
          facetName = cls.replace('ais-facet-', '');
        }
      });

      if (facetName) {
        var slug = slugifyFacetName(facetName);

        try {
          wrapper.dataset.facetAttribute = decodeURIComponent(facetName);
        } catch (e) {
          wrapper.dataset.facetAttribute = facetName;
        }

        if (slug) {
          wrapper.classList.add('teq-facet--' + slug);
        }
      }

      // ----- 2) Make header look like Shopify's <button class="facet-toggle"> -----
      var label = wrapper.querySelector('.ais-facet--header');
      var checkbox = wrapper.querySelector('.ais-dropdown-checkbox');

      // Only process once
      if (!label || label.dataset.teqFacetToggle === '1') return;

      var text = label.textContent.trim();

      // Mark as enhanced
      label.dataset.teqFacetToggle = '1';

      // Add Shopify facet classes
      label.classList.add('facet-toggle');

      // Replace label contents with Shopify-like structure
      label.innerHTML = [
        '<span class="facet-toggle-label">',
        text,
        '</span>',
        '<span class="angle angle-down">',
        '  <span class="angle-icon"></span>',
        '</span>',
      ].join('');

      var angleSpan = label.querySelector('.angle');

      // Keep aria-expanded and "open" class in sync with checkbox.
      // CSS: :checked + .ais-facet-dropdown-container { display: block }
      if (checkbox) {
        var updateExpanded = function () {
          var open = checkbox.checked;
          label.setAttribute('aria-expanded', open ? 'true' : 'false');
          wrapper.classList.toggle('teq-facet--open', open);

          if (angleSpan) {
            angleSpan.classList.toggle('angle-down', !open);
            angleSpan.classList.toggle('angle-up', open);
          }
        };

        updateExpanded();
        checkbox.addEventListener('change', updateExpanded);
      }
    });
  }


  const initObserver = (root) => {
    if (!root || typeof MutationObserver === 'undefined') return;

    var observer = new MutationObserver(function () {
      enhanceFacets(root);
    });

    observer.observe(root, { childList: true, subtree: true });
  }

  const start = () => {
    var root = document.querySelector(ROOT_SELECTOR);
    if (root) {
      enhanceFacets(root);
      initObserver(root);
      return;
    }

    // In case Algolia mounts later
    var attempts = 0;
    var maxAttempts = 20;
    var timer = setInterval(function () {
      attempts += 1;
      var el = document.querySelector(ROOT_SELECTOR);
      if (el || attempts >= maxAttempts) {
        clearInterval(timer);
        if (el) {
          enhanceFacets(el);
          initObserver(el);
        }
      }
    }, 250);
  }

  start();
}

const isAlgoliaColorFacet = (facet) => {
  if (!facet) {
    return false;
  }

  const name = String(facet.name || '').toLowerCase();
  const title = String(facet.title || '').toLowerCase();

  return (
    name === 'color' ||
    name === 'options.color' ||
    name === 'meta.custom.simple_color' ||
    name.endsWith('.color') ||
    name.endsWith('.simple_color') ||
    title === 'color'
  );
};

const isAlgoliaSizeFacet = (facet) => {
  if (!facet) {
    return false;
  }

  const name = String(facet.name || '').toLowerCase();
  const title = String(facet.title || '').toLowerCase();

  return (
    name === 'size' ||
    name === 'options.size' ||
    name === 'meta.shopify.size' ||
    name.endsWith('.size') ||
    title === 'size'
  );
};

const ALGOLIA_SIZE_SORT_ORDER = [
  'xxs', 'xs', 's', 'sm', 'small', 'm', 'md', 'medium', 'l', 'lg', 'large',
  'xl', 'x-large', 'xxl', '2xl', 'xxxl', '3xl', '1x', '2x', '3x', '4x', '5x',
  'os', 'one size', 'onesize', 'o/s',
];

const getAlgoliaSizeSortIndex = (value) => {
  const key = String(value || '').trim().toLowerCase();
  const indexed = ALGOLIA_SIZE_SORT_ORDER.indexOf(key);
  if (indexed > -1) {
    return indexed;
  }

  const numeric = parseFloat(key);
  if (Number.isFinite(numeric)) {
    return 1000 + numeric;
  }

  return 2000;
};

const sortAlgoliaSizeFacetItems = (items) =>
  [...items].sort((a, b) => {
    const aIndex = getAlgoliaSizeSortIndex(a.label || a.value);
    const bIndex = getAlgoliaSizeSortIndex(b.label || b.value);
    if (aIndex !== bIndex) {
      return aIndex - bIndex;
    }
    return String(a.label || a.value || '').localeCompare(String(b.label || b.value || ''), undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  });

const isAlgoliaBandFacet = (facet) => {
  if (!facet) {
    return false;
  }

  const name = String(facet.name || '').toLowerCase();
  const title = String(facet.title || '').toLowerCase();

  return name === 'options.band' || name === 'band' || title === 'band';
};

const isAlgoliaCupFacet = (facet) => {
  if (!facet) {
    return false;
  }

  const name = String(facet.name || '').toLowerCase();
  const title = String(facet.title || '').toLowerCase();

  return (
    name === 'options.cup' ||
    name === 'meta.shopify.cup-size' ||
    name === 'cup' ||
    title === 'cup'
  );
};

const isAlgoliaVariantFacet = (facet) =>
  isAlgoliaSizeFacet(facet) || isAlgoliaBandFacet(facet) || isAlgoliaCupFacet(facet);

const looksLikeAlgoliaJsonArrayLabel = (value) => {
  const trimmed = String(value || '').trim();
  return trimmed.startsWith('[') && trimmed.endsWith(']');
};

const formatAlgoliaColorFilterLabel = (label) => {
  const raw = String(label || '').trim();
  if (!raw) {
    return raw;
  }

  if (looksLikeAlgoliaJsonArrayLabel(raw)) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.filter(Boolean).join(', ');
      }
    } catch (error) {
      // Fall through to string cleanup.
    }
  }

  return raw.replaceAll('[', '').replaceAll(']', '').replaceAll('"', '').trim();
};

const algoliaFacetValuesMatch = (left, right, attribute) => {
  if (String(left) === String(right)) {
    return true;
  }

  if (isAlgoliaColorFacet({ name: attribute })) {
    return (
      formatAlgoliaColorFilterLabel(left).toLowerCase() ===
      formatAlgoliaColorFilterLabel(right).toLowerCase()
    );
  }

  return false;
};

const algoliaVariantFacetDistinctCountCache = new Map();
const algoliaVariantFacetBatchFetched = new Set();
let algoliaVariantFacetCountGeneration = 0;
let algoliaVariantFacetCountTimer = null;

const getAlgoliaInstantSearchInstance = () =>
  window.algoliaShopify?.instantsearch?.search || window.algoliaShopify?.instantsearch;

const mergeAlgoliaRefinementLists = (...sources) => {
  const merged = {};

  sources.forEach((source) => {
    Object.entries(source || {}).forEach(([attribute, values]) => {
      if (!Array.isArray(values) || !values.length) {
        return;
      }

      if (!merged[attribute]) {
        merged[attribute] = [];
      }

      values.forEach((value) => {
        const normalized = String(value);
        if (!merged[attribute].includes(normalized)) {
          merged[attribute].push(normalized);
        }
      });
    });
  });

  return merged;
};

const parseAlgoliaRefinementListFromUrl = () => {
  const parsed = {};

  try {
    const params = new URLSearchParams(window.location.search);
    params.forEach((value, key) => {
      const match = key.match(/^refinementList\[(.+)\](?:\[(\d+)\])?$/);
      if (!match) {
        return;
      }

      const attribute = decodeURIComponent(match[1]);
      if (!parsed[attribute]) {
        parsed[attribute] = [];
      }
      if (!parsed[attribute].includes(value)) {
        parsed[attribute].push(value);
      }
    });
  } catch (error) {
    // ignore
  }

  return parsed;
};

const parseAlgoliaRefinementListFromDom = () => {
  const parsed = {};

  document
    .querySelectorAll(
      '#algolia-shopify-instantsearch .ais-current-refined-values--link[data-attribute]'
    )
    .forEach((link) => {
      const attribute = link.getAttribute('data-attribute');
      const value =
        link.getAttribute('data-value') || link.getAttribute('data-label');
      if (!attribute || !value) {
        return;
      }

      if (!parsed[attribute]) {
        parsed[attribute] = [];
      }
      if (!parsed[attribute].includes(value)) {
        parsed[attribute].push(value);
      }
    });

  return parsed;
};

const getAlgoliaHelperRefinementList = () => {
  const search = getAlgoliaInstantSearchInstance();
  const helpers = [
    search?.helper,
    window.algoliaShopify?.instantsearch?.helper,
    window.algoliaShopify?.instantsearch?.search?.helper,
  ].filter(Boolean);

  const lists = [];

  helpers.forEach((helper) => {
    const state = helper?.state;
    if (!state) {
      return;
    }

    lists.push(state.disjunctiveFacetsRefinements);
    lists.push(state.facetsRefinements);
  });

  return mergeAlgoliaRefinementLists(...lists);
};

const getAlgoliaUiStateRefinementList = () => {
  const search = getAlgoliaInstantSearchInstance();
  if (!search || typeof search.getUiState !== 'function') {
    return {};
  }

  try {
    const allState = search.getUiState() || {};
    const indexId =
      search.mainIndex?.getIndexId?.() ||
      search.mainIndex?.getIndexName?.() ||
      Object.keys(allState)[0] ||
      '';
    const indexState =
      (indexId && allState[indexId]) || Object.values(allState)[0] || {};

    return indexState.refinementList || {};
  } catch (error) {
    return {};
  }
};

const getAlgoliaActiveRefinementList = () =>
  mergeAlgoliaRefinementLists(
    parseAlgoliaRefinementListFromUrl(),
    getAlgoliaUiStateRefinementList(),
    getAlgoliaHelperRefinementList(),
    parseAlgoliaRefinementListFromDom()
  );

// Helper/uiState only — avoids stale URL/DOM refinements zeroing facet counts on clear.
const getAlgoliaFacetCountRefinementList = () => {
  const helperList = getAlgoliaHelperRefinementList();
  if (Object.keys(helperList).length) {
    return helperList;
  }

  return getAlgoliaUiStateRefinementList();
};

// Always patch on collection PLPs so band/cup/size/color show distinct product
// counts (not variant record counts). Helper/uiState-only refinements avoid the
// stale URL/DOM zero-count bug when clearing filters.
const isAlgoliaFacetCountsHidden = () => Boolean(window.__natoriAlgoliaHideFacetCounts);

const shouldApplyAlgoliaDistinctFacetCounts = () =>
  !isAlgoliaFacetCountsHidden() && Boolean(getAlgoliaCollectionFilterString());

const getAlgoliaInstantSearchUiState = () => {
  const search = getAlgoliaInstantSearchInstance();
  const indexId =
    search?.mainIndex?.getIndexId?.() ||
    search?.mainIndex?.getIndexName?.() ||
    '';

  return {
    indexId,
    refinementList: getAlgoliaActiveRefinementList(),
  };
};

const getAlgoliaCollectionFilterString = () => {
  const handle =
    window.__natoriAlgoliaCollectionHandle ||
    window.location.pathname.match(/^\/collections\/([^/?#]+)\/?$/i)?.[1];

  if (!handle) {
    return '';
  }

  return `collections:"${String(handle).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
};

const getAlgoliaVariantFacetCountSignature = (attribute) => {
  const refinementList = getAlgoliaFacetCountRefinementList();
  const parts = Object.entries(refinementList)
    .filter(([attr]) => attr !== attribute)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([attr, values]) => `${attr}=${(values || []).slice().sort().join('|')}`);

  const collectionFilter = getAlgoliaCollectionFilterString();
  if (collectionFilter) {
    parts.unshift(`collection=${collectionFilter}`);
  }

  return parts.join(';');
};

const buildAlgoliaDistinctFacetFilters = (attribute, value, refinementList) => {
  const facetFilters = [];

  Object.entries(refinementList || {}).forEach(([attr, values]) => {
    if (attr === attribute || !values?.length) {
      return;
    }

    facetFilters.push(values.map((entry) => `${attr}:${entry}`));
  });

  facetFilters.push([`${attribute}:${value}`]);
  return facetFilters;
};

const applyAlgoliaDistinctVariantFacetCounts = (attribute, items) => {
  if (!shouldApplyAlgoliaDistinctFacetCounts()) {
    return items;
  }

  const signature = getAlgoliaVariantFacetCountSignature(attribute);

  return items.map((item) => {
    const cacheKey = `${attribute}|${item.value}|${signature}`;
    const cached = algoliaVariantFacetDistinctCountCache.get(cacheKey);
    return cached != null ? { ...item, count: cached } : item;
  });
};

const patchAlgoliaVariantFacetCountDom = (attribute, value, count) => {
  const wrappers = document.querySelectorAll(
    '#algolia-shopify-instantsearch .ais-facet-dropdown-wrapper'
  );

  wrappers.forEach((wrapper) => {
    const facetAttribute = wrapper.dataset.facetAttribute;
    if (facetAttribute !== attribute) {
      return;
    }

    wrapper.querySelectorAll('.ais-RefinementList-item').forEach((item) => {
      const input = item.querySelector('input[type="checkbox"]');
      const label = item.querySelector('.ais-RefinementList-label, .ais-facet--label');
      const candidates = [
        input?.value,
        item.getAttribute('data-value'),
        label?.childNodes?.[0]?.textContent?.trim(),
      ].filter(Boolean);

      if (isAlgoliaColorFacet({ name: attribute }) && label) {
        const labelTextEl = label.querySelector('.ais-RefinementList-labelText');
        if (labelTextEl?.textContent?.trim()) {
          candidates.push(labelTextEl.textContent.trim());
        } else {
          const clone = label.cloneNode(true);
          clone
            .querySelectorAll(
              '.ais-RefinementList-count, .ais-facet--count-distinct, .ais-RefinementList-checkbox, input'
            )
            .forEach((el) => el.remove());
          const cleaned = clone.textContent?.trim();
          if (cleaned) {
            candidates.push(cleaned);
          }
        }
      }

      const matched = candidates.some((candidate) =>
        algoliaFacetValuesMatch(candidate, value, attribute)
      );
      if (!matched) {
        return;
      }

      const countEl = item.querySelector(
        '.ais-RefinementList-count, .ais-facet--count-distinct'
      );
      if (countEl) {
        countEl.textContent = String(count);
      }
    });
  });
};

const getAlgoliaFacetValuesMap = (results) => {
  const map = {};

  if (results?.facets && typeof results.facets === 'object' && !Array.isArray(results.facets)) {
    Object.entries(results.facets).forEach(([attribute, values]) => {
      if (values && typeof values === 'object') {
        map[attribute] = values;
      }
    });
  }

  (results?.disjunctiveFacets || []).forEach((facet) => {
    if (facet?.name && facet?.data && typeof facet.data === 'object') {
      map[facet.name] = facet.data;
    }
  });

  return map;
};

const refreshAlgoliaVariantFacetDistinctCounts = async () => {
  if (!shouldApplyAlgoliaDistinctFacetCounts()) {
    return;
  }

  const search = getAlgoliaInstantSearchInstance();
  const client = search?.client || window.algoliaShopify?.searchClient;
  const results = search?.helper?.lastResults;
  const facetValuesMap = getAlgoliaFacetValuesMap(results);

  if (!client?.search || !Object.keys(facetValuesMap).length) {
    return;
  }

  const generation = ++algoliaVariantFacetCountGeneration;
  const refinementList = getAlgoliaFacetCountRefinementList();
  const indexName =
    search.mainIndex?.getIndexName?.() ||
    `${window.algoliaShopify?.config?.index_prefix || 'shopify_'}products`;
  const collectionFilter = getAlgoliaCollectionFilterString();
  const requests = [];
  const requestMeta = [];

  Object.keys(facetValuesMap).forEach((attribute) => {
    if (
      !isAlgoliaBandFacet({ name: attribute }) &&
      !isAlgoliaCupFacet({ name: attribute }) &&
      !isAlgoliaSizeFacet({ name: attribute }) &&
      !isAlgoliaColorFacet({ name: attribute })
    ) {
      return;
    }

    const signature = getAlgoliaVariantFacetCountSignature(attribute);
    const batchKey = `${attribute}|${signature}`;
    const facetValues = facetValuesMap[attribute] || {};

    if (algoliaVariantFacetBatchFetched.has(batchKey)) {
      Object.keys(facetValues).forEach((value) => {
        const cached = algoliaVariantFacetDistinctCountCache.get(
          `${attribute}|${value}|${signature}`
        );
        if (cached != null) {
          patchAlgoliaVariantFacetCountDom(attribute, value, cached);
        }
      });
      return;
    }

    Object.keys(facetValues).forEach((value) => {
      const cacheKey = `${attribute}|${value}|${signature}`;

      const params = {
        query: results.query || '',
        hitsPerPage: 0,
        distinct: true,
        facetFilters: buildAlgoliaDistinctFacetFilters(attribute, value, refinementList),
      };

      if (collectionFilter) {
        params.filters = collectionFilter;
      }

      requests.push({
        indexName,
        params,
      });
      requestMeta.push({ attribute, value, cacheKey });
    });
  });

  if (!requests.length) {
    return;
  }

  try {
    const response = await client.search(requests);
    if (generation !== algoliaVariantFacetCountGeneration) {
      return;
    }

    const attributesUpdated = new Set();

    response.results.forEach((result, index) => {
      const meta = requestMeta[index];
      if (!meta) {
        return;
      }

      const count = result?.nbHits;
      if (count == null) {
        return;
      }

      algoliaVariantFacetDistinctCountCache.set(meta.cacheKey, count);
      patchAlgoliaVariantFacetCountDom(meta.attribute, meta.value, count);
      attributesUpdated.add(meta.attribute);
    });

    attributesUpdated.forEach((attribute) => {
      algoliaVariantFacetBatchFetched.add(
        `${attribute}|${getAlgoliaVariantFacetCountSignature(attribute)}`
      );
    });
  } catch (error) {
    console.error('Algolia variant facet distinct count refresh failed', error);
  }
};

const setupAlgoliaVariantFacetDistinctCounts = () => {
  if (isAlgoliaFacetCountsHidden()) {
    return;
  }

  let lastRefinementSignature = '';

  const scheduleRefresh = () => {
    const refinementSignature = JSON.stringify(getAlgoliaFacetCountRefinementList());
    if (refinementSignature !== lastRefinementSignature) {
      lastRefinementSignature = refinementSignature;
      algoliaVariantFacetCountGeneration += 1;
      algoliaVariantFacetBatchFetched.clear();
      algoliaVariantFacetDistinctCountCache.clear();
    }

    if (algoliaVariantFacetCountTimer) {
      clearTimeout(algoliaVariantFacetCountTimer);
    }

    algoliaVariantFacetCountTimer = setTimeout(() => {
      algoliaVariantFacetCountTimer = null;
      refreshAlgoliaVariantFacetDistinctCounts();
    }, 120);
  };

  document.addEventListener('algolia.instantSearch.onRender', scheduleRefresh);
  scheduleRefresh();
};

const isAlgoliaPriceFacet = (facet) => {
  if (!facet) {
    return false;
  }

  const name = String(facet.name || '').toLowerCase();
  const title = String(facet.title || '').toLowerCase();

  return (
    facet.type === 'slider' ||
    name === 'price' ||
    name.includes('price') ||
    title === 'price'
  );
};

const formatAlgoliaRefinementLabel = (text) => {
  const raw = String(text || '').trim();
  if (!raw) {
    return raw;
  }

  const separatorIndex = raw.indexOf(':');
  if (separatorIndex > -1) {
    const attribute = raw.slice(0, separatorIndex).trim();
    const value = raw.slice(separatorIndex + 1).trim();
    const formattedValue = formatAlgoliaColorFilterLabel(value);

    if (formattedValue !== value) {
      return `${attribute} : ${formattedValue}`;
    }
  }

  return formatAlgoliaColorFilterLabel(raw);
};

const setupAlgoliaFacetParamsOptions = () => {
  algoliaShopify.hooks.registerHook('beforeInstantSearchFacetParamsOptions', (params, facet) => {
    if (isAlgoliaColorFacet(facet)) {
      return {
        ...params,
        // Keep facet order stable when a value is selected (don't jump refined items to top).
        sortBy: ['name:asc'],
        transformItems(items) {
          const mapped = items.map((item) => ({
            ...item,
            label: formatAlgoliaColorFilterLabel(item.label),
          }));
          return applyAlgoliaDistinctVariantFacetCounts(facet.name, mapped);
        },
      };
    }

    if (isAlgoliaSizeFacet(facet)) {
      return {
        ...params,
        sortBy: ['name:asc'],
        transformItems(items) {
          const sorted = sortAlgoliaSizeFacetItems(items);
          return applyAlgoliaDistinctVariantFacetCounts(facet.name, sorted);
        },
      };
    }

    if (isAlgoliaBandFacet(facet) || isAlgoliaCupFacet(facet)) {
      return {
        ...params,
        sortBy: ['name:asc'],
        transformItems(items) {
          return applyAlgoliaDistinctVariantFacetCounts(facet.name, items);
        },
      };
    }

    if (isAlgoliaPriceFacet(facet)) {
      return {
        ...params,
        pips: true,
        tooltips: false,
      };
    }

    return params;
  });
};const ALGOLIA_ACTIVE_CURRENCY = null;
const ALGOLIA_MONEY_FORMAT = "{{amount}}";
const ALGOLIA_PRICE_CURRENCY_SYMBOL = "$";

const ALGOLIA_CURRENCY_LOCALES = {
  GBP: 'en-GB',
  USD: 'en-US',
  EUR: 'de-DE',
  CAD: 'en-CA',
  AUD: 'en-AU',
  CHF: 'de-CH',
  JPY: 'ja-JP',
};

const ALGOLIA_CURRENCY_SYMBOLS = {
  GBP: '£',
  USD: '$',
  EUR: '€',
  CAD: '$',
  AUD: '$',
  CHF: 'CHF',
  JPY: '¥',
};

const getAlgoliaActiveCurrencyCode = () => {
  if (typeof Shopify !== 'undefined' && Shopify.currency?.active) {
    return Shopify.currency.active;
  }

  return ALGOLIA_ACTIVE_CURRENCY || 'USD';
};

const getAlgoliaMoneyFormat = () => {
  const activeCurrency = getAlgoliaActiveCurrencyCode();

  if (
    typeof Shopify !== 'undefined' &&
    Shopify.money_format &&
    (!Shopify.currency?.active || Shopify.currency.active === activeCurrency)
  ) {
    return Shopify.money_format;
  }

  if (ALGOLIA_MONEY_FORMAT && ALGOLIA_MONEY_FORMAT !== 'null') {
    return ALGOLIA_MONEY_FORMAT;
  }

  const symbol = getAlgoliaCurrencySymbol();
  return `${symbol}`;
};

const getAlgoliaCurrencySymbol = () => {
  const activeCurrency = getAlgoliaActiveCurrencyCode();

  if (ALGOLIA_CURRENCY_SYMBOLS[activeCurrency]) {
    return ALGOLIA_CURRENCY_SYMBOLS[activeCurrency];
  }

  if (ALGOLIA_PRICE_CURRENCY_SYMBOL && ALGOLIA_PRICE_CURRENCY_SYMBOL !== 'null') {
    return ALGOLIA_PRICE_CURRENCY_SYMBOL;
  }

  const themeSymbol = window.theme?.settings?.currency_symbol;
  if (themeSymbol) {
    return themeSymbol;
  }

  const moneyFormat =
    window.theme?.settings?.money_format ||
    window.theme?.settings?.money_with_currency_format;
  if (typeof moneyFormat === 'string') {
    const symbolMatch = moneyFormat.match(/[\$£€¥₹]|CHF|R\$/);
    if (symbolMatch) {
      return symbolMatch[0];
    }
  }

  return '$';
};

let algoliaPriceInputTimer;

const getAlgoliaPriceSearch = () => window.algoliaShopify?.instantsearch?.search;

const getAlgoliaPriceFacetPanel = () =>
  document.querySelector('#algolia-shopify-instantsearch .ais-facet-price .ais-Panel-body > div');

const getAlgoliaPriceRangeSlider = () =>
  document.querySelector('#algolia-shopify-instantsearch .ais-facet-price .ais-RangeSlider');

const getAlgoliaPriceHandles = () => {
  const panel = document.querySelector('#algolia-shopify-instantsearch .ais-facet-price');
  if (!panel) {
    return null;
  }

  return {
    lower: panel.querySelector('.rheostat-handle-lower'),
    upper: panel.querySelector('.rheostat-handle-upper'),
  };
};

const readAlgoliaPriceBounds = (handles) => {
  if (!handles?.lower || !handles?.upper) {
    return null;
  }

  return {
    min: Number(handles.lower.getAttribute('aria-valuemin')),
    max: Number(handles.upper.getAttribute('aria-valuemax')),
    currentMin: Number(handles.lower.getAttribute('aria-valuenow')),
    currentMax: Number(handles.upper.getAttribute('aria-valuenow')),
  };
};

const readAlgoliaPriceUiStateRange = () => {
  const search = getAlgoliaPriceSearch();
  if (!search?.mainIndex) {
    return null;
  }

  let uiState = {};
  try {
    if (typeof search.getUiState === 'function') {
      const indexId =
        search.mainIndex.getIndexId?.() ||
        search.mainIndex.getIndexName?.() ||
        '';
      const allState = search.getUiState() || {};
      uiState = (indexId && allState[indexId]) || Object.values(allState)[0] || {};
    } else {
      uiState = search.mainIndex.getWidgetUiState({}) || {};
    }
  } catch (error) {
    try {
      uiState = search.mainIndex.getWidgetUiState({}) || {};
    } catch (innerError) {
      return null;
    }
  }

  const raw = uiState?.range?.price;
  if (!raw || typeof raw !== 'string' || !raw.includes(':')) {
    return null;
  }

  // InstantSearch open ranges look like "70:" or ":100"
  const [minRaw, maxRaw] = raw.split(':');
  const min = minRaw === '' ? null : Number(minRaw);
  const max = maxRaw === '' ? null : Number(maxRaw);
  const hasMin = Number.isFinite(min);
  const hasMax = Number.isFinite(max);
  if (!hasMin && !hasMax) {
    return null;
  }

  return {
    min: hasMin ? min : null,
    max: hasMax ? max : null,
  };
};

const applyAlgoliaPriceRange = (min, max, bounds) => {
  const search = getAlgoliaPriceSearch();
  if (!search?.mainIndex?.setIndexUiState || !bounds) {
    return;
  }

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return;
  }

  let nextMin = Math.max(bounds.min, Math.min(min, bounds.max));
  let nextMax = Math.min(bounds.max, Math.max(max, bounds.min));

  if (!Number.isFinite(nextMin) || !Number.isFinite(nextMax)) {
    return;
  }

  if (nextMin > nextMax) {
    const swap = nextMin;
    nextMin = nextMax;
    nextMax = swap;
  }

  // Always merge into previous uiState so Size/Color/etc. refinements are preserved.
  if (nextMin <= bounds.min && nextMax >= bounds.max) {
    search.mainIndex.setIndexUiState((prev) => {
      const next = { ...(prev || {}) };
      const range = { ...(next.range || {}) };
      delete range.price;
      if (Object.keys(range).length) {
        next.range = range;
      } else {
        delete next.range;
      }
      return next;
    });
    return;
  }

  search.mainIndex.setIndexUiState((prev) => ({
    ...(prev || {}),
    range: {
      ...((prev && prev.range) || {}),
      price: `${nextMin}:${nextMax}`,
    },
  }));
};

const bindAlgoliaPriceFacetInputs = (inputsRoot) => {
  const minInput = inputsRoot.querySelector('.algolia-price-input-min');
  const maxInput = inputsRoot.querySelector('.algolia-price-input-max');
  if (!minInput || !maxInput) {
    return;
  }

  const setEditing = (editing) => {
    inputsRoot.__algoliaPriceEditing = Boolean(editing);
  };

  const isEditingInputs = () =>
    Boolean(inputsRoot.__algoliaPriceEditing) ||
    document.activeElement === minInput ||
    document.activeElement === maxInput;

  /**
   * Keep min/max fields aligned with InstantSearch / rheostat.
   * Never overwrite while the user is actively editing either field.
   */
  const syncInputsFromSlider = (force = false, { fromHandles = false } = {}) => {
    if (isEditingInputs() && !force) {
      return;
    }

    const bounds = readAlgoliaPriceBounds(getAlgoliaPriceHandles());
    if (!bounds) {
      return;
    }

    const uiRange = readAlgoliaPriceUiStateRange();
    let currentMin;
    let currentMax;

    if (fromHandles) {
      currentMin = bounds.currentMin;
      currentMax = bounds.currentMax;

      // During InstantSearch re-render, handles can briefly reset to the full
      // range while uiState still has the refined values — prefer uiState then.
      if (
        uiRange &&
        currentMin <= bounds.min &&
        currentMax >= bounds.max &&
        ((uiRange.min != null && uiRange.min > bounds.min) ||
          (uiRange.max != null && uiRange.max < bounds.max))
      ) {
        currentMin = uiRange.min != null ? uiRange.min : bounds.min;
        currentMax = uiRange.max != null ? uiRange.max : bounds.max;
      }
    } else if (uiRange) {
      currentMin = uiRange.min != null ? uiRange.min : bounds.min;
      currentMax = uiRange.max != null ? uiRange.max : bounds.max;
    } else {
      currentMin = bounds.min;
      currentMax = bounds.max;
    }

    if (!Number.isFinite(currentMin)) {
      currentMin = bounds.min;
    }
    if (!Number.isFinite(currentMax)) {
      currentMax = bounds.max;
    }

    minInput.min = bounds.min;
    minInput.max = bounds.max;
    maxInput.min = bounds.min;
    maxInput.max = bounds.max;

    if (minInput.value !== String(currentMin)) {
      minInput.value = String(currentMin);
    }
    if (maxInput.value !== String(currentMax)) {
      maxInput.value = String(currentMax);
    }
  };

  inputsRoot.__algoliaSyncPriceInputs = syncInputsFromSlider;

  const readInputRange = () => {
    const bounds = readAlgoliaPriceBounds(getAlgoliaPriceHandles());
    if (!bounds) {
      return null;
    }

    const minRaw = String(minInput.value).trim();
    const maxRaw = String(maxInput.value).trim();

    // Allow temporary empty fields while editing — do not coerce yet.
    if (minRaw === '' || maxRaw === '') {
      return { incomplete: true, bounds };
    }

    let min = parseInt(minRaw, 10);
    let max = parseInt(maxRaw, 10);

    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      return { incomplete: true, bounds };
    }

    return { min, max, bounds };
  };

  const applyFromInputs = () => {
    const parsed = readInputRange();
    if (!parsed || parsed.incomplete) {
      // Restore legal values if the user left a field empty on blur/Enter.
      setEditing(false);
      syncInputsFromSlider(true);
      return;
    }

    applyAlgoliaPriceRange(parsed.min, parsed.max, parsed.bounds);
    setEditing(false);
    requestAnimationFrame(() => syncInputsFromSlider(true));
  };

  const attachHandleObserver = () => {
    const panel = document.querySelector('#algolia-shopify-instantsearch .ais-facet-price');
    const rheostat = panel?.querySelector('.rheostat');
    const handles = getAlgoliaPriceHandles();
    if (!panel || !handles?.lower || !handles?.upper) {
      return;
    }

    const observeTarget = rheostat || panel;
    if (
      inputsRoot.algoliaObservedTarget === observeTarget &&
      inputsRoot.algoliaObservedLower === handles.lower &&
      inputsRoot.algoliaObservedUpper === handles.upper &&
      inputsRoot.algoliaPriceObserver
    ) {
      return;
    }

    if (inputsRoot.algoliaPriceObserver) {
      inputsRoot.algoliaPriceObserver.disconnect();
      inputsRoot.algoliaPriceObserver = null;
    }

    let syncTimer;
    const queueHandleSync = () => {
      if (isEditingInputs()) {
        return;
      }
      if (syncTimer) {
        clearTimeout(syncTimer);
      }
      // Debounce — rheostat mutates style + aria rapidly while dragging.
      syncTimer = setTimeout(() => {
        if (isEditingInputs()) {
          return;
        }
        syncInputsFromSlider(false, { fromHandles: true });
      }, 16);
    };

    const observer = new MutationObserver(queueHandleSync);
    observer.observe(observeTarget, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-valuenow', 'aria-valuemin', 'aria-valuemax', 'style', 'class'],
    });
    inputsRoot.algoliaPriceObserver = observer;
    inputsRoot.algoliaObservedTarget = observeTarget;
    inputsRoot.algoliaObservedLower = handles.lower;
    inputsRoot.algoliaObservedUpper = handles.upper;

    if (!inputsRoot.dataset.algoliaSliderPointerBound) {
      inputsRoot.dataset.algoliaSliderPointerBound = '1';
      const syncAfterPointer = () => {
        if (isEditingInputs()) {
          return;
        }
        const run = () => {
          if (isEditingInputs()) {
            return;
          }
          if (readAlgoliaPriceUiStateRange()) {
            syncInputsFromSlider(true);
          } else {
            syncInputsFromSlider(false, { fromHandles: true });
          }
        };
        setTimeout(run, 0);
        setTimeout(run, 50);
      };
      panel.addEventListener('pointerup', syncAfterPointer);
      panel.addEventListener('touchend', syncAfterPointer);
      panel.addEventListener('mouseup', syncAfterPointer);
    }
  };

  if (inputsRoot.dataset.algoliaPriceBound === '1') {
    attachHandleObserver();
    if (!isEditingInputs()) {
      syncInputsFromSlider(true);
    }
    return;
  }

  inputsRoot.dataset.algoliaPriceBound = '1';

  [minInput, maxInput].forEach((input) => {
    input.addEventListener('focus', () => {
      setEditing(true);
    });
    input.addEventListener('input', () => {
      // Keep temporary values while typing — commit on blur / Enter only.
      setEditing(true);
    });
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        applyFromInputs();
        input.blur();
      }
    });
    input.addEventListener('blur', () => {
      applyFromInputs();
    });
  });

  attachHandleObserver();
  syncInputsFromSlider(true);
};

const updateAlgoliaPriceCurrencyLabels = (inputsRoot) => {
  if (!inputsRoot) {
    return;
  }

  const symbol = getAlgoliaCurrencySymbol();
  inputsRoot.querySelectorAll('.field-currency').forEach((element) => {
    if (element.textContent !== symbol) {
      element.textContent = symbol;
    }
  });
};

const ensureAlgoliaPriceFacetInputs = () => {
  const panelContent = getAlgoliaPriceFacetPanel();
  const rangeSlider = getAlgoliaPriceRangeSlider();
  if (!panelContent || !rangeSlider) {
    return;
  }

  const currencySymbol = getAlgoliaCurrencySymbol();
  let inputsRoot = panelContent.querySelector('.algolia-price-input');
  if (!inputsRoot) {
    inputsRoot = document.createElement('div');
    inputsRoot.className = 'price_slider_amount algolia-price-input';
    inputsRoot.innerHTML = `
      <div class="field">
        <span class="field-currency">${currencySymbol}</span>
        <input type="number" class="algolia-price-input-min" inputmode="numeric" step="1" aria-label="Minimum price">
      </div>
      <div class="field">
        <span class="field-currency">${currencySymbol}</span>
        <input type="number" class="algolia-price-input-max" inputmode="numeric" step="1" aria-label="Maximum price">
      </div>
    `;
    rangeSlider.insertAdjacentElement('beforebegin', inputsRoot);
  } else {
    updateAlgoliaPriceCurrencyLabels(inputsRoot);
    // Keep numeric fields above the slider for readability while dragging.
    if (inputsRoot.nextElementSibling !== rangeSlider) {
      rangeSlider.insertAdjacentElement('beforebegin', inputsRoot);
    }
  }

  bindAlgoliaPriceFacetInputs(inputsRoot);

  // Force-sync after Clear Refinements / InstantSearch re-renders (never while typing).
  if (
    typeof inputsRoot.__algoliaSyncPriceInputs === 'function' &&
    !(
      inputsRoot.__algoliaPriceEditing ||
      document.activeElement?.classList?.contains('algolia-price-input-min') ||
      document.activeElement?.classList?.contains('algolia-price-input-max')
    )
  ) {
    inputsRoot.__algoliaSyncPriceInputs(true);
  }
};

const setupAlgoliaPriceFacetInputs = () => {
  document.addEventListener('algolia.instantSearch.onRender', () => {
    if (algoliaPriceInputTimer) {
      clearTimeout(algoliaPriceInputTimer);
    }

    algoliaPriceInputTimer = setTimeout(() => {
      ensureAlgoliaPriceFacetInputs();

      const inputsRoot = document.querySelector(
        '#algolia-shopify-instantsearch .algolia-price-input'
      );
      if (typeof inputsRoot?.__algoliaSyncPriceInputs !== 'function') {
        return;
      }

      if (
        inputsRoot.__algoliaPriceEditing ||
        document.activeElement?.classList?.contains('algolia-price-input-min') ||
        document.activeElement?.classList?.contains('algolia-price-input-max')
      ) {
        return;
      }

      const sync = inputsRoot.__algoliaSyncPriceInputs;
      const run = () => {
        if (readAlgoliaPriceUiStateRange()) {
          sync(true);
        } else {
          sync(false, { fromHandles: true });
        }
      };
      run();
      requestAnimationFrame(run);
    }, 50);
  });
};

let algoliaSortPlacementTimer;

const ensureAlgoliaSortPlacement = () => {
  const root = document.querySelector('#algolia-shopify-instantsearch');
  const facets = root?.querySelector('.ais-facets');
  if (!facets) {
    return;
  }

  const headerSort = root.querySelector('.ais-search-header .ais-sort');
  const slotSort = facets.querySelector('.algolia-sort-slot .ais-sort');
  const toolbarSort = root.querySelector('.algolia-mobile-toolbar .ais-sort');
  const sort = headerSort || slotSort || toolbarSort;
  if (!sort) {
    return;
  }

  const isMobile = window.matchMedia('(max-width: 767px)').matches;
  const facetsButton = root.querySelector('.ais-facets-button');
  const changeDisplay = root.querySelector('.ais-change-display');
  if (changeDisplay && document.querySelector('#main-content[data-template="collection"]')) {
    changeDisplay.setAttribute('hidden', 'hidden');
  }

  let sortSlot = root.querySelector('.algolia-sort-slot');
  if (!sortSlot) {
    sortSlot = document.createElement('div');
    sortSlot.className = 'thb-filter-sort-count algolia-sort-slot';
  }

  let sortWrapper = sortSlot.querySelector('.thb-filter-sort');
  if (!sortWrapper) {
    sortWrapper = document.createElement('div');
    sortWrapper.className = 'thb-filter-sort';
    sortSlot.appendChild(sortWrapper);
  }

  let selectWrap = sortWrapper.querySelector('.select');
  if (!selectWrap) {
    selectWrap = document.createElement('div');
    selectWrap.className = 'select';
    sortWrapper.appendChild(selectWrap);
  }

  if (headerSort && slotSort && headerSort !== slotSort) {
    slotSort.remove();
  }
  if (headerSort && toolbarSort && headerSort !== toolbarSort) {
    toolbarSort.remove();
  }
  if (slotSort && toolbarSort && slotSort !== toolbarSort) {
    toolbarSort.remove();
  }

  Array.from(sort.childNodes).forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
      node.remove();
    }
  });

  if (!selectWrap.contains(sort)) {
    selectWrap.appendChild(sort);
  }

  const select = sort.querySelector('.ais-SortBy-select');
  if (select) {
    select.id = 'AlgoliaSortBy';
    select.classList.add('facet-filters__sort', 'select__select');
  }

  let mobileToolbar = root.querySelector('.algolia-mobile-toolbar');
  if (isMobile && facetsButton) {
    if (!mobileToolbar) {
      mobileToolbar = document.createElement('div');
      mobileToolbar.className = 'algolia-mobile-toolbar';
      facetsButton.parentNode.insertBefore(mobileToolbar, facetsButton);
      mobileToolbar.appendChild(facetsButton);
    }

    if (!mobileToolbar.contains(sortSlot)) {
      mobileToolbar.appendChild(sortSlot);
    }
  } else {
    if (mobileToolbar) {
      if (facetsButton && facetsButton.parentNode === mobileToolbar) {
        mobileToolbar.parentNode.insertBefore(facetsButton, mobileToolbar);
      }
      mobileToolbar.remove();
    }

    if (!facets.contains(sortSlot)) {
      facets.appendChild(sortSlot);
    }
  }

  root.classList.add('algolia-sort-placed');
};

const setupAlgoliaSortPlacement = () => {
  const scheduleSortPlacement = () => {
    if (algoliaSortPlacementTimer) {
      clearTimeout(algoliaSortPlacementTimer);
    }

    algoliaSortPlacementTimer = setTimeout(ensureAlgoliaSortPlacement, 50);
  };

  document.addEventListener('algolia.instantSearch.onRender', scheduleSortPlacement);
  window.addEventListener('resize', scheduleSortPlacement);
  scheduleSortPlacement();
};

const ALGOLIA_REFINEMENT_REMOVE_SVG = `
  <svg width="7" height="7" viewBox="0 0 7 7" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M6.4731 6.875C6.60027 6.875 6.72968 6.82592 6.82561 6.72775C7.01972 6.53364 7.01972 6.21682 6.82561 6.02049L4.67929 3.87416L6.82561 1.72784C7.01972 1.53373 7.01972 1.21692 6.82561 1.02058C6.63151 0.826473 6.31469 0.826473 6.11835 1.02058L3.97203 3.1669L1.82794 1.02504C1.63383 0.830936 1.31701 0.830936 1.12068 1.02504C0.926571 1.21915 0.926571 1.53596 1.12068 1.7323L3.267 3.87863L1.12291 6.02049C0.928802 6.21459 0.928802 6.53141 1.12291 6.72775C1.22108 6.82592 1.34825 6.875 1.47542 6.875C1.6026 6.875 1.732 6.82592 1.82794 6.72775L3.97426 4.58142L6.12058 6.72775C6.21652 6.82592 6.34593 6.875 6.4731 6.875Z" fill="var(--color-accent)"></path>
  </svg>
`;

const decodeAlgoliaChipText = (value) => {
  let raw = String(value || '').trim();
  if (!raw) {
    return '';
  }

  // Algolia often stores labels/operators as URI-encoded UTF-8 (e.g. %E2%89%A5%2068 → "≥ 68").
  for (let i = 0; i < 3; i += 1) {
    if (!/%[0-9A-Fa-f]{2}/.test(raw)) {
      break;
    }
    try {
      const decoded = decodeURIComponent(raw.replace(/\+/g, ' '));
      if (decoded === raw) {
        break;
      }
      raw = decoded.trim();
    } catch (error) {
      break;
    }
  }

  return raw;
};

const isAlgoliaPriceChipLink = (link) => {
  if (!link) {
    return false;
  }
  const attribute = (link.getAttribute('data-attribute') || '').toLowerCase();
  const type = (link.getAttribute('data-type') || '').toLowerCase();
  return (
    attribute === 'price' ||
    attribute.endsWith('.price') ||
    attribute.includes('price') ||
    (type === 'numeric' && attribute === 'price')
  );
};

const formatAlgoliaChipMoney = (amount) => {
  const formatted = teqFormatPriceFromRaw(amount);
  if (formatted) {
    return formatted;
  }

  const num = Number(amount);
  if (!Number.isFinite(num)) {
    return '';
  }

  const symbol = getAlgoliaCurrencySymbol();
  const formattedAmount = num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${symbol}${formattedAmount}`;
};

const formatAlgoliaChipLabel = (link) => {
  const rawLabel = decodeAlgoliaChipText(
    link.getAttribute('data-label') || link.getAttribute('data-value') || link.textContent || ''
  );

  if (isAlgoliaPriceChipLink(link)) {
    const value = Number(link.getAttribute('data-value'));
    if (Number.isFinite(value)) {
      return formatAlgoliaChipMoney(value);
    }
  }

  return formatAlgoliaColorFilterLabel(rawLabel);
};

const setAlgoliaChipContent = (link, label, signature = label) => {
  if (!link || !label) {
    return;
  }

  if (link.dataset.algoliaChipSignature === signature && link.querySelector('.algolia-refinement-remove')) {
    // Keep decoding data-label in sync even when markup is already good.
    if (link.getAttribute('data-label') !== label) {
      link.setAttribute('data-label', label);
    }
    return;
  }

  link.dataset.algoliaChipSignature = signature;
  link.setAttribute('data-label', label);
  link.innerHTML = `${label}<span class="algolia-refinement-remove">${ALGOLIA_REFINEMENT_REMOVE_SVG}</span>`;
};

const formatAlgoliaPriceChipFallback = (link) => {
  const value = Number(link.getAttribute('data-value'));
  if (Number.isFinite(value)) {
    setAlgoliaChipContent(link, formatAlgoliaChipMoney(value));
    return;
  }

  // Last resort: decode "≥ 68" / "≤ 140" style labels instead of leaving %E2%89%A5%2068.
  const decoded = decodeAlgoliaChipText(
    link.getAttribute('data-label') || link.textContent || ''
  );
  const cleaned = decoded
    .replace(/^price\s*:?\s*/i, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (cleaned) {
    setAlgoliaChipContent(link, cleaned);
  }
};

const mergeAlgoliaPriceRefinementChips = () => {
  const list = document.querySelector(
    '#algolia-shopify-instantsearch .ais-current-refined-values--list'
  );
  if (!list) {
    return;
  }

  const priceItems = [...list.querySelectorAll('.ais-current-refined-values--item')].filter((item) =>
    isAlgoliaPriceChipLink(item.querySelector('.ais-current-refined-values--link'))
  );

  if (!priceItems.length) {
    return;
  }

  let min = null;
  let max = null;
  priceItems.forEach((item) => {
    const link = item.querySelector('.ais-current-refined-values--link');
    if (!link) {
      return;
    }
    // Un-hide items if Algolia re-rendered a fresh list.
    item.hidden = false;
    item.removeAttribute('aria-hidden');

    const operator = decodeAlgoliaChipText(link.getAttribute('data-operator') || '');
    const value = Number(link.getAttribute('data-value'));
    if (!Number.isFinite(value)) {
      return;
    }
    if (operator.includes('>')) {
      min = value;
    } else if (operator.includes('<')) {
      max = value;
    } else if (min == null) {
      min = value;
    } else {
      max = value;
    }
  });

  const uiRange = readAlgoliaPriceUiStateRange();
  if (uiRange) {
    if (Number.isFinite(uiRange.min)) {
      min = uiRange.min;
    }
    if (Number.isFinite(uiRange.max)) {
      max = uiRange.max;
    }
  }

  const handles = getAlgoliaPriceHandles();
  const bounds = readAlgoliaPriceBounds(handles);

  // Production shows a single "$min-$max" chip. Fill open ends from slider bounds when available.
  const displayMin = Number.isFinite(min) ? min : bounds?.min ?? 0;
  const displayMax = Number.isFinite(max) ? max : bounds?.max;

  if (!Number.isFinite(displayMax)) {
    // Bounds not ready yet — still decode/format each chip so "%E2%89%A5%2068" never sticks.
    priceItems.forEach((item) => {
      const link = item.querySelector('.ais-current-refined-values--link');
      if (link) {
        formatAlgoliaPriceChipFallback(link);
      }
    });
    return;
  }

  const combinedLabel = `${formatAlgoliaChipMoney(displayMin)} - ${formatAlgoliaChipMoney(displayMax)}`;
  const primaryItem = priceItems[0];
  const primaryLink = primaryItem.querySelector('.ais-current-refined-values--link');
  if (!primaryLink) {
    return;
  }

  priceItems.slice(1).forEach((item) => {
    item.hidden = true;
    item.setAttribute('aria-hidden', 'true');
  });

  setAlgoliaChipContent(primaryLink, combinedLabel, `price:${combinedLabel}`);

  if (!primaryLink.dataset.algoliaPriceChipBound) {
    primaryLink.dataset.algoliaPriceChipBound = '1';
    primaryLink.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const liveHandles = getAlgoliaPriceHandles();
      const liveBounds = readAlgoliaPriceBounds(liveHandles);
      if (!liveBounds) {
        priceItems.forEach((item) => {
          const link = item.querySelector('.ais-current-refined-values--link');
          if (link && link !== primaryLink) {
            link.click();
          }
        });
        return;
      }
      applyAlgoliaPriceRange(liveBounds.min, liveBounds.max, liveBounds);
    });
  }
};

const setupAlgoliaCurrentRefinementsFormatter = () => {
  const enhanceCurrentRefinementChips = () => {
    document
      .querySelectorAll('#algolia-shopify-instantsearch .ais-current-refined-values--link')
      .forEach((link) => {
        // Price chips are merged separately to match production "$min-$max".
        if (isAlgoliaPriceChipLink(link)) {
          // Decode immediately so encoded labels never flash if merge waits on bounds.
          if (/%[0-9A-Fa-f]{2}/.test(link.getAttribute('data-label') || link.textContent || '')) {
            formatAlgoliaPriceChipFallback(link);
          }
          return;
        }

        const formattedLabel = formatAlgoliaChipLabel(link);
        if (!formattedLabel) {
          return;
        }

        setAlgoliaChipContent(link, formattedLabel);
      });

    mergeAlgoliaPriceRefinementChips();

    // Catch any leftover encoded labels in CurrentRefinements / legacy widgets.
    document
      .querySelectorAll(
        '#algolia-shopify-instantsearch .ais-current-refined-values--link, #algolia-shopify-instantsearch .ais-CurrentRefinements-categoryLabel, #algolia-shopify-instantsearch .ais-CurrentRefinements-label, #algolia-shopify-instantsearch .ais-current-refined-values--value'
      )
      .forEach((element) => {
        const original = element.textContent.trim();
        if (!/%[0-9A-Fa-f]{2}/.test(original) && !original.includes('≥') && !original.includes('≤')) {
          // Still decode if attribute label is encoded but text already customized.
          return;
        }
        if (/%[0-9A-Fa-f]{2}/.test(original)) {
          const decoded = decodeAlgoliaChipText(original);
          if (decoded && decoded !== original) {
            if (element.classList.contains('ais-current-refined-values--link')) {
              formatAlgoliaPriceChipFallback(element);
            } else {
              element.textContent = decoded;
            }
          }
        }
      });
  };

  let scheduled = false;
  const scheduleEnhance = () => {
    if (scheduled) {
      return;
    }
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      enhanceCurrentRefinementChips();
      // Retry once so open price ranges can merge after rheostat mounts.
      window.setTimeout(enhanceCurrentRefinementChips, 50);
    });
  };

  document.addEventListener('algolia.instantSearch.onRender', scheduleEnhance);

  const root = document.querySelector('#algolia-shopify-instantsearch');
  if (root && typeof MutationObserver === 'function') {
    const observer = new MutationObserver((mutations) => {
      const touched = mutations.some((mutation) => {
        const target = mutation.target;
        if (!(target instanceof Element)) {
          return false;
        }
        return (
          target.closest('.ais-current-refined-values') ||
          target.classList?.contains('ais-current-refined-values--link') ||
          [...(mutation.addedNodes || [])].some(
            (node) =>
              node instanceof Element &&
              (node.classList?.contains('ais-current-refined-values--link') ||
                node.querySelector?.('.ais-current-refined-values--link'))
          )
        );
      });
      if (touched) {
        scheduleEnhance();
      }
    });
    observer.observe(root, { childList: true, subtree: true, characterData: true });
  }

  scheduleEnhance();
};

// ---- TEQ: Force grid view on mobile ----------------------
const setupMobileGridView = () => {
  var BREAKPOINT = 749; // Mobile breakpoint in px

  const applyGridOnMobile = () => {
    if (typeof window === 'undefined') return;

    // get the ais-page wrapper
    var page = document.querySelector('.ais-page');
    if (!page) return;

    if (window.innerWidth <= BREAKPOINT) {
      // Remove list-view class so layout is grid/block
      page.classList.remove('ais-results-as-list');

      // Fix the toggle buttons' visual + aria state
      var blockBtn = document.querySelector('.ais-change-display-block');
      var listBtn = document.querySelector('.ais-change-display-list');

      if (blockBtn) {
        blockBtn.classList.add('ais-change-display-selected');
        blockBtn.setAttribute('aria-pressed', 'true');
      }

      if (listBtn) {
        listBtn.classList.remove('ais-change-display-selected');
        listBtn.setAttribute('aria-pressed', 'false');
      }
    }
  }

  // Run once now and again on resize/orientation change
  if (typeof window !== 'undefined') {
    applyGridOnMobile();
    window.addEventListener('resize', applyGridOnMobile);
    window.addEventListener('orientationchange', applyGridOnMobile);
  }
}

const setupTaxifyRescan = () => {
  if (typeof window === 'undefined') return;

  // Try once immediately
  const tryRescan = () => {
    var taxify = window.Taxify;
    if (!taxify || typeof taxify.rescanAllElements !== 'function') {
      return false;
    }

    try {
      taxify.rescanAllElements();
      
      // console.log('Taxify rescanAllElements called');
    } catch (e) {
      console.error('Taxify rescanAllElements failed', e);
    }
    return true;
  }

  // If it works immediately, return
  if (tryRescan()) return;

  // Else, poll a few times in case Taxify loads later
  var attempts = 0;
  var maxAttempts = 20; // ~5 seconds at 250ms
  var timer = setInterval(function () {
    attempts += 1;
    if (tryRescan() || attempts >= maxAttempts) {
      clearInterval(timer);
    }
  }, 250);
}

const teqNormalizePriceToCents = (raw) => {
  if (raw == null) return null;

  // If Algolia gives us a number, it's a price in currency units (e.g. 130.8 => €130.80)
  if (typeof raw === 'number') {
    return Math.round(raw * 100);
  }

  // If it's a string, clean it and treat it as currency units too
  if (typeof raw === 'string') {
    var str = raw.trim();
    if (!str) return null;

    // Keep only digits, comma, dot, minus
    str = str.replace(/[^0-9,.-]/g, '');
    if (!str) return null;

    // Normalize comma to dot for parsing
    str = str.replace(',', '.');

    var n = parseFloat(str);
    if (!isFinite(n)) return null;

    // n is in euros → convert to cents
    return Math.round(n * 100);
  }

  return null;
}

const teqFormatPriceFromRaw = (raw) => {
  var cents = teqNormalizePriceToCents(raw);
  if (cents == null) return '';

  const moneyFormat = getAlgoliaMoneyFormat();
  const activeCurrency = getAlgoliaActiveCurrencyCode();

  try {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, moneyFormat);
    }
  } catch (e) {
    console.error('TEQ formatMoney error', e);
  }

  var amount = cents / 100;
  try {
    const locale = ALGOLIA_CURRENCY_LOCALES[activeCurrency] || 'en-US';
    var formatted = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: activeCurrency,
      maximumFractionDigits: 2,
    }).format(amount);
    formatted = formatted.replace(/\.00(?=\D*$)/, '');

    return formatted;
  } catch (e) {
    return `${getAlgoliaCurrencySymbol()}${amount.toFixed(2)}`;
  }
}

window.teqNormalizePriceToCents = teqNormalizePriceToCents;
window.teqFormatPriceFromRaw = teqFormatPriceFromRaw;

const teqDisplayPriceFromHit = (hit, distinct) => {
  if (!hit) return '';

  // Optional: show a range on distinct hits if Algolia provides min/max
  if (
    distinct &&
    hit.variants_min_price != null &&
    hit.variants_max_price != null &&
    hit.variants_min_price !== hit.variants_max_price
  ) {
    var minFormatted = teqFormatPriceFromRaw(hit.variants_min_price);
    var maxFormatted = teqFormatPriceFromRaw(hit.variants_max_price);

    if (minFormatted && maxFormatted && minFormatted !== maxFormatted) {
      return minFormatted + ' – ' + maxFormatted;
    }
  }

  // Normal case: format the variant’s own price
  return teqFormatPriceFromRaw(hit.price);
}

const getAlgoliaReviewsAverage = (hit) => {
  if (!hit) return null;

  const raw =
    hit?.meta?.yotpo?.reviews_average ??
    hit?.mfield_yotpo_reviews_average ??
    hit?.['meta.yotpo.reviews_average'];

  if (raw == null || raw === '') return null;

  const average = parseFloat(raw);
  return Number.isFinite(average) && average > 0 ? average : null;
};

const buildAlgoliaReviewsTemplate = (hit, html) => {
  const average = getAlgoliaReviewsAverage(hit);
  if (average == null) return '';

  return html`
    <div class="plpRatings">
      <div
        class="star-rating"
        style="--star-rating: ${average};"
        role="img"
        aria-label="${average} out of 5 stars"
      ></div>
    </div>
  `;
};

const ALGOLIA_CARD_IMAGE_WIDTHS = [345, 596, 800, 1200];
const ALGOLIA_CARD_IMAGE_SIZES = '(min-width: 768px) 25vw, 50vw';

const getAlgoliaHitImageUrl = (hit) =>
  hit?.product_image || hit?.image || algoliaShopify?.helpers?.mediumImage?.(hit) || '';

const buildAlgoliaResponsiveImageAttrs = (imageUrl) => {
  if (!imageUrl) {
    return { src: '', srcset: '', sizes: ALGOLIA_CARD_IMAGE_SIZES };
  }

  const [path, query] = String(imageUrl).split('?');
  const cleanPath = path.replace(
    /_(pico|icon|thumb|small|compact|medium|large|grande|\d+x\d+)(?=\.(jpg|jpeg|png|webp|gif))/i,
    ''
  );
  const querySuffix = query ? `&${query}` : '';
  const buildUrl = (width) => `${cleanPath}?width=${width}${querySuffix}`;
  const srcset = ALGOLIA_CARD_IMAGE_WIDTHS.map((width) => `${buildUrl(width)} ${width}w`).join(', ');

  return {
    src: buildUrl(596),
    srcset,
    sizes: ALGOLIA_CARD_IMAGE_SIZES,
  };
};

const buildAlgoliaSwatchImageValue = (imageUrl) => {
  if (!imageUrl) {
    return '';
  }

  const attrs = buildAlgoliaResponsiveImageAttrs(imageUrl);
  return attrs.srcset || attrs.src;
};

const normalizeAlgoliaVariantUrl = (url) => {
  if (!url) {
    return '';
  }

  return String(url).replace(
    /variant=gid:\/\/shopify\/ProductVariant\/(\d+)/i,
    'variant=$1'
  );
};

const getAlgoliaVariantIdFromGid = (gid) => {
  if (!gid) {
    return null;
  }

  const match = String(gid).match(/(\d+)$/);
  return match ? parseInt(match[1], 10) : null;
};

let algoliaSwatchTimer = null;

const getAlgoliaColorSwatchLookup = () => {
  if (window.NatoriColorSwatches?.getColorSwatchLookup) {
    return window.NatoriColorSwatches.getColorSwatchLookup();
  }
  if (!window.settings?.color_swatches) {
    return { keyArray: [], valArray: [] };
  }
  const customColorsArray = window.settings.color_swatches.split(',').map((color) => {
    const idx = color.indexOf(':');
    if (idx === -1) return { key: color.trim().toLowerCase(), value: '' };
    return { key: color.slice(0, idx).trim().toLowerCase(), value: color.slice(idx + 1).trim() || '' };
  });
  return {
    keyArray: customColorsArray.map((color) => color.key),
    valArray: customColorsArray.map((color) => color.value),
  };
};

const resolveAlgoliaSwatchColorStyle = (colorName, swatchColor, keyArray, valArray) => {
  let colorValue = String(colorName || '').toLowerCase().trim();
  const customColorIndex = keyArray.indexOf(colorValue);
  if (customColorIndex > -1) {
    colorValue = valArray[customColorIndex];
  }

  let bgValue = false;
  if (swatchColor) {
    const swatchParts = String(swatchColor).split('-');
    const lastPart = swatchParts[swatchParts.length - 1].trim();
    if (lastPart.includes('#')) {
      colorValue = lastPart;
    } else {
      bgValue = lastPart;
      colorValue = 'var(--bg-body)';
    }
  } else if ((window.NatoriColorSwatches && window.NatoriColorSwatches.isSwatchImageValue) ? window.NatoriColorSwatches.isSwatchImageValue(colorValue) : colorValue.includes('.')) {
    bgValue = colorValue;
    colorValue = 'var(--bg-body)';
  }

  const shopDomain = window.location.hostname;
  const bgImageUrl = bgValue
    ? ((window.NatoriColorSwatches && window.NatoriColorSwatches.swatchBackgroundImageCss)
        ? window.NatoriColorSwatches.swatchBackgroundImageCss(bgValue, shopDomain)
        : `--option-color-image: url('//${shopDomain}/cdn/shop/files/${bgValue}');`)
    : '';

  return { colorValue, bgImageUrl };
};

const parseAlgoliaAvailableColorSwatches = (hit) => {
  const raw =
    hit?.meta?.custom?.available_color_swatches ??
    hit?.mfield_custom_available_color_swatches ??
    null;

  if (!raw) {
    return null;
  }

  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Array.isArray(parsed)) {
      return parsed;
    }
    return parsed?.swatches || null;
  } catch (error) {
    return null;
  }
};

const ALGOLIA_SWATCH_VISIBLE_LIMIT = 6;

const renderAlgoliaSwatchTemplate = (swatchData, html) => {
  const styleParts = [];
  if (swatchData.inlineStyle) {
    styleParts.push(swatchData.inlineStyle.replace(/;+\s*$/, ''));
  } else {
    styleParts.push(`--option-color: ${swatchData.colorValue}`);
    if (swatchData.bgImageUrl) {
      styleParts.push(swatchData.bgImageUrl);
    }
  }

  const overflowClass = swatchData.overflow ? 'product-card-swatch--overflow' : '';
  const priceAttr = swatchData.price != null && swatchData.price !== '' ? String(swatchData.price) : '';
  const compareAttr =
    swatchData.compareAtPrice != null && swatchData.compareAtPrice !== ''
      ? String(swatchData.compareAtPrice)
      : '';

  return html`<div
    class="product-card-swatch ${swatchData.active} ${overflowClass}"
    style="${styleParts.join('; ')}; "
    data-srcset="${swatchData.primaryImage}"
    data-srcset-2="${swatchData.secondaryImage}"
    data-srcset-alt="${swatchData.primaryImageAlt}"
    data-srcset-2-alt="${swatchData.secondaryImageAlt}"
    data-href="${swatchData.url}"
    data-quan="${swatchData.available}"
    data-price="${priceAttr}"
    data-compare-at-price="${compareAttr}"
  >
    <span class="visually-hidden">${swatchData.color}</span>
  </div>`;
};

const resolveAlgoliaHitCompareAt = (hit) => {
  if (!hit) return null;
  if (hit.variants_compare_at_price_max != null && hit.variants_compare_at_price_max !== '') {
    return hit.variants_compare_at_price_max;
  }
  if (hit.compare_at_price != null && hit.compare_at_price !== '') {
    return hit.compare_at_price;
  }
  if (hit.variants_compare_at_price_min != null && hit.variants_compare_at_price_min !== '') {
    return hit.variants_compare_at_price_min;
  }
  return null;
};

const resolveAlgoliaSwatchCompareAt = (swatchPrice, swatchCompare, hit) => {
  const priceCents = teqNormalizePriceToCents(swatchPrice);
  const explicitCompare =
    swatchCompare != null && swatchCompare !== '' ? swatchCompare : null;

  if (explicitCompare != null) {
    const explicitCents = teqNormalizePriceToCents(explicitCompare);
    if (
      explicitCents != null &&
      priceCents != null &&
      explicitCents > priceCents
    ) {
      return explicitCompare;
    }
    // Explicit compare that isn't higher than price → not on sale for this swatch.
    if (explicitCents != null) {
      return '';
    }
  }

  const hitCompare = resolveAlgoliaHitCompareAt(hit);
  const hitCompareCents = teqNormalizePriceToCents(hitCompare);
  if (
    hitCompare != null &&
    hitCompareCents != null &&
    priceCents != null &&
    hitCompareCents > priceCents
  ) {
    return hitCompare;
  }

  return '';
};

const mapAlgoliaSwatchData = (swatch, hit, keyArray, valArray, defaultPrimaryImage) => {
  const color = swatch.color || swatch.label || '';
  const styleFromMetafield = swatch.style ? String(swatch.style).trim() : '';
  const { colorValue, bgImageUrl } = styleFromMetafield
    ? { colorValue: null, bgImageUrl: null }
    : resolveAlgoliaSwatchColorStyle(color, swatch.swatchColor, keyArray, valArray);
  const swatchVariantId = getAlgoliaVariantIdFromGid(swatch.variantId);
  const swatchProductId = String(swatch.productId || '').replace('gid://shopify/Product/', '');
  const primaryImageValue =
    swatch.primaryImage || swatch.image || (swatchProductId === String(hit.id) ? defaultPrimaryImage : '');
  const secondaryImageValue = swatch.secondaryImage || swatch.image2 || '';
  const price = swatch.price ?? swatch.variantPrice ?? '';
  const compareAtPrice = resolveAlgoliaSwatchCompareAt(
    price,
    swatch.compareAtPrice ?? swatch.compare_at_price ?? swatch.compareAt ?? '',
    hit
  );

  return {
    color,
    colorValue,
    bgImageUrl,
    inlineStyle: styleFromMetafield,
    active:
      ((swatchVariantId && String(swatchVariantId) === String(hit.objectID)) ||
        swatchProductId === String(hit.id))
        ? 'active'
        : '',
    primaryImage: buildAlgoliaSwatchImageValue(primaryImageValue) || primaryImageValue,
    secondaryImage: buildAlgoliaSwatchImageValue(secondaryImageValue) || secondaryImageValue,
    primaryImageAlt: swatch.primaryImageAlt || '',
    secondaryImageAlt: swatch.secondaryImageAlt || '',
    url: normalizeAlgoliaVariantUrl(swatch.url || ''),
    available: swatch.available !== false,
    price,
    compareAtPrice,
  };
};

const getAlgoliaSwatchDataFromHit = (hit, productUrl, imageUrl) => {
  const metafieldSwatches = parseAlgoliaAvailableColorSwatches(hit);
  if (!Array.isArray(metafieldSwatches) || !metafieldSwatches.length) {
    return [];
  }

  const { keyArray, valArray } = getAlgoliaColorSwatchLookup();
  const responsiveImage = buildAlgoliaResponsiveImageAttrs(imageUrl);
  const defaultPrimaryImage = responsiveImage.srcset || responsiveImage.src || '';

  return metafieldSwatches.map((swatch) =>
    mapAlgoliaSwatchData(swatch, hit, keyArray, valArray, defaultPrimaryImage)
  );
};

const buildAlgoliaSwatchTemplatesFromHit = (hit, productUrl, imageUrl, html) => {
  const swatchDataList = getAlgoliaSwatchDataFromHit(hit, productUrl, imageUrl);
  // Keep the initially active (current product) swatch at the front of the list
  // so it stays visible before the "+ More" overflow cut.
  const activeIndex = swatchDataList.findIndex((swatch) => swatch.active === 'active');
  if (activeIndex > 0) {
    const [activeSwatch] = swatchDataList.splice(activeIndex, 1);
    swatchDataList.unshift(activeSwatch);
  }
  const visibleSwatches = swatchDataList.slice(0, ALGOLIA_SWATCH_VISIBLE_LIMIT);
  const overflowSwatches = swatchDataList.slice(ALGOLIA_SWATCH_VISIBLE_LIMIT);
  const templates = visibleSwatches.map((swatchData) => renderAlgoliaSwatchTemplate(swatchData, html));

  if (overflowSwatches.length) {
    templates.push(html`<button
      type="button"
      class="product-card-swatch-more morebtn"
      aria-expanded="false"
      aria-label="Show ${overflowSwatches.length} more color options"
    ><span class="product-card-swatch-more__desktop">+ MORE</span><span class="product-card-swatch-more__mobile" aria-hidden="true">+</span></button>`);
    overflowSwatches.forEach((swatchData) => {
      templates.push(renderAlgoliaSwatchTemplate({ ...swatchData, overflow: true }, html));
    });
  }

  return templates;
};

const bindAlgoliaProductCardSwatches = (productCard) => {
  if (typeof productCard.enableSwatches === 'function') {
    productCard.enableSwatches();
  }
};

const initializeAlgoliaProductCardSwatch = (productCard) => {
  if (typeof productCard.setDefaultActiveSwatch === 'function') {
    productCard.setDefaultActiveSwatch({ preferPersisted: true });
    return;
  }

  if (typeof productCard.enableSwatches === 'function') {
    productCard.enableSwatches();
  }
};

const initializeAllAlgoliaProductCardSwatches = () => {
  document
    .querySelectorAll('#algolia-shopify-instantsearch product-card[data-source-file="algolia-instant-search"]')
    .forEach((productCard) => {
      initializeAlgoliaProductCardSwatch(productCard);
    });
};

const setupAlgoliaProductCardSecondaryImages = () => {
  initializeAllAlgoliaProductCardSwatches();
};

const getAlgoliaProductHandleFromUrl = (url) => {
  if (!url) {
    return '';
  }

  try {
    const path = String(url).startsWith('http')
      ? new URL(url).pathname
      : String(url).split('?')[0];
    const match = path.match(/\/products\/([^/?#]+)/i);
    return match ? decodeURIComponent(match[1]) : '';
  } catch (error) {
    return '';
  }
};

const ALGOLIA_REFINEMENT_OPTION_NAMES = {
  'options.band': ['band'],
  'options.cup': ['cup'],
  'options.size': ['size'],
  'meta.shopify.size': ['size'],
  'meta.shopify.cup-size': ['cup', 'cup-size'],
  'meta.custom.simple_color': ['color', 'colour'],
  'options.color': ['color', 'colour'],
};

const normalizeAlgoliaOptionValue = (value, attribute) => {
  const attributeName = String(attribute || '').toLowerCase();
  if (
    attributeName.includes('color') ||
    attributeName.includes('simple_color')
  ) {
    return formatAlgoliaColorFilterLabel(String(value || ''));
  }

  return String(value || '').trim();
};

const getAlgoliaHitColorValue = (hit) => {
  const raw =
    hit?.meta?.custom?.simple_color ??
    hit?.['meta.custom.simple_color'] ??
    hit?.options?.color ??
    hit?.option1 ??
    '';

  return formatAlgoliaColorFilterLabel(String(raw || ''));
};

const ALGOLIA_OPTION_NAME_ALIASES = {
  band: ['band', 'band size', 'bra band'],
  cup: ['cup', 'cup size', 'bra cup'],
  size: ['size'],
  color: ['color', 'colour', 'couleur'],
};

const normalizeAlgoliaOptionCompareValue = (value) => {
  const formatted = formatAlgoliaColorFilterLabel(String(value || ''))
    .trim()
    .toLowerCase();
  const numeric = parseFloat(formatted);
  if (Number.isFinite(numeric) && /^-?\d+(\.\d+)?$/.test(formatted)) {
    return String(numeric);
  }
  return formatted;
};

const algoliaOptionNameMatches = (optionName, requirementNames) => {
  const normalized = String(optionName || '').trim().toLowerCase();
  return requirementNames.some((name) => {
    const aliases = ALGOLIA_OPTION_NAME_ALIASES[name] || [name];
    return aliases.some(
      (alias) => normalized === alias || normalized.includes(alias)
    );
  });
};

const getAlgoliaRequiredVariantOptions = (hit, refinementListOverride) => {
  const required = [];
  const seen = new Set();
  const refinementList = refinementListOverride || getAlgoliaActiveRefinementList();

  Object.entries(refinementList || {}).forEach(([attribute, values]) => {
    const optionNames = ALGOLIA_REFINEMENT_OPTION_NAMES[attribute];
    if (!optionNames || !values?.length) {
      return;
    }

    const signature = `${attribute}:${values[0]}`;
    if (seen.has(signature)) {
      return;
    }
    seen.add(signature);

    required.push({
      names: optionNames,
      value: normalizeAlgoliaOptionValue(values[0], attribute),
    });
  });

  if (hit?._distinct) {
    const colorValue = getAlgoliaHitColorValue(hit);
    const hasColorRequirement = required.some((entry) =>
      entry.names.some((name) => name === 'color' || name === 'colour')
    );

    if (colorValue && !hasColorRequirement) {
      required.push({ names: ['color', 'colour'], value: colorValue });
    }
  }

  return required.filter((entry) => entry.value);
};

const getAlgoliaHitVariantOptions = (hit) => {
  const entries = [];
  const add = (names, value, attribute) => {
    const normalized = normalizeAlgoliaOptionValue(value, attribute);
    if (normalized) {
      entries.push({ names, value: normalized });
    }
  };

  add(
    ['color', 'colour'],
    hit?.meta?.custom?.simple_color ??
      hit?.['meta.custom.simple_color'] ??
      hit?.options?.color ??
      hit?.option1,
    'meta.custom.simple_color'
  );
  add(['band'], hit?.options?.band ?? hit?.option2, 'options.band');
  add(['cup', 'cup-size'], hit?.options?.cup ?? hit?.option3, 'options.cup');
  add(['size'], hit?.options?.size, 'options.size');

  return entries;
};

const algoliaOptionRequirementMatches = (requirement, candidateEntries) => {
  const target = normalizeAlgoliaOptionCompareValue(requirement.value);

  return candidateEntries.some(
    (entry) =>
      entry.names.some((name) =>
        algoliaOptionNameMatches(name, requirement.names)
      ) && normalizeAlgoliaOptionCompareValue(entry.value) === target
  );
};

const hitSatisfiesAlgoliaRequiredOptions = (hit, required) => {
  if (!required.length) {
    return false;
  }

  const hitOptions = getAlgoliaHitVariantOptions(hit);
  return required.every((requirement) =>
    algoliaOptionRequirementMatches(requirement, hitOptions)
  );
};

const buildAlgoliaProductVariantUrl = (handle, variantId) => {
  if (!handle) {
    return '';
  }

  const base = `/products/${handle}`;
  const numericId = getAlgoliaVariantIdFromGid(variantId) || variantId;
  return numericId ? `${base}?variant=${numericId}` : base;
};

const getAlgoliaVariantSelectedOptionEntries = (variant) =>
  (variant?.selectedOptions || []).map((option) => ({
    names: [String(option.name || '').toLowerCase()],
    value: formatAlgoliaColorFilterLabel(String(option.value || '')),
  }));

const algoliaVariantMatchesRequirement = (variant, requirement) => {
  const entries = getAlgoliaVariantSelectedOptionEntries(variant);
  const target = normalizeAlgoliaOptionCompareValue(requirement.value);

  return entries.some(
    (entry) =>
      algoliaOptionNameMatches(entry.names[0], requirement.names) &&
      normalizeAlgoliaOptionCompareValue(entry.value) === target
  );
};

const findAlgoliaFirstMatchingVariant = (product, required) => {
  const variants = (product?.variants?.edges || [])
    .map((edge) => edge?.node)
    .filter(Boolean);

  if (!variants.length || !required.length) {
    return null;
  }

  const available = variants.filter(
    (variant) =>
      (variant.quantityAvailable ?? 0) > 0 &&
      required.every((requirement) =>
        algoliaVariantMatchesRequirement(variant, requirement)
      )
  );

  if (available.length) {
    return available[0];
  }

  return (
    variants.find((variant) =>
      required.every((requirement) =>
        algoliaVariantMatchesRequirement(variant, requirement)
      )
    ) || null
  );
};

const getAlgoliaHitContextFromCard = (card) => ({
  _distinct: card.getAttribute('data-distinct') === 'true',
  handle: card.getAttribute('data-handle') || '',
  meta: {
    custom: {
      simple_color: card.getAttribute('data-hit-color') || '',
    },
  },
});

const getAlgoliaSwatchColorLabel = (swatch) => {
  const hidden = swatch?.querySelector('.visually-hidden')?.textContent?.trim();
  if (hidden) {
    return formatAlgoliaColorFilterLabel(hidden);
  }
  return '';
};

const algoliaVariantLinkProductCache = {
  byHandle: new Map(),
  byId: new Map(),
};

const mergeAlgoliaVariantLinkProductCache = (productsByHandle, productsById) => {
  (productsByHandle || new Map()).forEach((product, handle) => {
    if (handle && product) {
      algoliaVariantLinkProductCache.byHandle.set(handle, product);
    }
  });
  (productsById || new Map()).forEach((product, id) => {
    if (id && product) {
      algoliaVariantLinkProductCache.byId.set(id, product);
      if (product.handle) {
        algoliaVariantLinkProductCache.byHandle.set(product.handle, product);
      }
    }
  });
};

const buildAlgoliaHitContextForSwatch = (productCard, swatch, swatchHandle) => ({
  _distinct: productCard.getAttribute('data-distinct') === 'true',
  handle: swatchHandle,
  meta: {
    custom: {
      simple_color:
        getAlgoliaSwatchColorLabel(swatch) ||
        productCard.getAttribute('data-hit-color') ||
        '',
    },
  },
});

const applyAlgoliaFilteredVariantLinkForSwatch = (productCard, swatch) => {
  if (!productCard || !swatch) {
    return '';
  }

  const swatchHref = swatch.getAttribute('data-href') || '';
  const swatchHandle = getAlgoliaProductHandleFromUrl(swatchHref);
  if (!swatchHandle) {
    return swatchHref;
  }

  const refinementList = getAlgoliaActiveRefinementList();
  const hit = buildAlgoliaHitContextForSwatch(productCard, swatch, swatchHandle);
  const required = getAlgoliaRequiredVariantOptions(hit, refinementList);

  if (!required.length) {
    return swatchHref;
  }

  const product = algoliaVariantLinkProductCache.byHandle.get(swatchHandle);
  if (!product) {
    return swatchHref;
  }

  const matchingVariant = findAlgoliaFirstMatchingVariant(product, required);
  if (!matchingVariant?.id) {
    return swatchHref;
  }

  const url = buildAlgoliaProductVariantUrl(swatchHandle, matchingVariant.id);
  const variantId = getAlgoliaVariantIdFromGid(matchingVariant.id);

  swatch.setAttribute('data-href', url);
  productCard.dataset.filteredVariantUrl = url;
  if (variantId) {
    productCard.dataset.variantId = String(variantId);
  }

  return url;
};

const applyAlgoliaFilteredVariantLinksToCard = (
  productCard,
  productsByHandle,
  refinementListOverride
) => {
  const refinementList = refinementListOverride || getAlgoliaActiveRefinementList();
  const swatches = productCard.querySelectorAll('.product-card-swatch[data-href]');
  let activeUrl = '';

  swatches.forEach((swatch) => {
    const swatchHref = swatch.getAttribute('data-href') || '';
    const swatchHandle = getAlgoliaProductHandleFromUrl(swatchHref);
    if (!swatchHandle) {
      return;
    }

    const product = productsByHandle.get(swatchHandle);
    if (!product) {
      return;
    }

    const hit = buildAlgoliaHitContextForSwatch(productCard, swatch, swatchHandle);
    const required = getAlgoliaRequiredVariantOptions(hit, refinementList);
    if (!required.length) {
      return;
    }

    const matchingVariant = findAlgoliaFirstMatchingVariant(product, required);
    if (!matchingVariant?.id) {
      return;
    }

    const url = buildAlgoliaProductVariantUrl(swatchHandle, matchingVariant.id);
    swatch.setAttribute('data-href', url);

    if (swatch.classList.contains('active')) {
      activeUrl = url;
      const variantId = getAlgoliaVariantIdFromGid(matchingVariant.id);
      if (variantId) {
        productCard.dataset.variantId = String(variantId);
      }
    }
  });

  if (!swatches.length) {
    const handle = productCard.getAttribute('data-handle');
    const product = handle ? productsByHandle.get(handle) : null;
    if (product) {
      const hit = getAlgoliaHitContextFromCard(productCard);
      const required = getAlgoliaRequiredVariantOptions(hit, refinementList);
      const matchingVariant = findAlgoliaFirstMatchingVariant(product, required);
      if (matchingVariant?.id) {
        activeUrl = buildAlgoliaProductVariantUrl(handle, matchingVariant.id);
        const variantId = getAlgoliaVariantIdFromGid(matchingVariant.id);
        if (variantId) {
          productCard.dataset.variantId = String(variantId);
        }
      }
    }
  }

  if (!activeUrl) {
    const activeSwatch =
      productCard.querySelector('.product-card-swatch.active[data-href]') ||
      productCard.querySelector('.product-card-swatch[data-href]');
    activeUrl = activeSwatch?.getAttribute('data-href') || '';
  }

  if (activeUrl) {
    productCard.dataset.filteredVariantUrl = activeUrl;
    productCard.dataset.selectedSwatchHref = activeUrl;
    productCard.dataset.defaultSwatchHref = activeUrl;
    syncAlgoliaCardProductLinks(productCard, activeUrl);
  }
};

const resolveAlgoliaProductForCard = (productCard, productsByHandle, productsById) => {
  const handle = productCard.getAttribute('data-handle');
  const productId = String(productCard.getAttribute('data-product-id') || '');

  if (handle && productsByHandle.has(handle)) {
    return productsByHandle.get(handle);
  }

  if (productId && productsById.has(productId)) {
    return productsById.get(productId);
  }

  return null;
};

const syncAlgoliaCardProductLinks = (productCard, url) => {
  if (!productCard || !url) {
    return;
  }

  productCard
    .querySelectorAll('a.product-featured-image-link, a.product-card-title')
    .forEach((link) => {
      link.setAttribute('href', url);
    });
};

const clearAlgoliaFilteredVariantLinks = (productCard) => {
  if (!productCard) {
    return;
  }

  delete productCard.dataset.filteredVariantUrl;
  delete productCard.dataset.selectedSwatchHref;
  delete productCard.dataset.defaultSwatchHref;
};

const applyAlgoliaFilteredVariantLinks = (
  productCard,
  product,
  hit,
  requiredOverride,
  productsByHandle
) => {
  const map =
    productsByHandle ||
    (product?.handle
      ? new Map([[product.handle, product]])
      : algoliaVariantLinkProductCache.byHandle);

  if (product?.handle && !map.has(product.handle)) {
    map.set(product.handle, product);
  }

  const required = requiredOverride || getAlgoliaRequiredVariantOptions(hit);
  if (!required.length) {
    clearAlgoliaFilteredVariantLinks(productCard);
    return;
  }

  applyAlgoliaFilteredVariantLinksToCard(productCard, map, getAlgoliaActiveRefinementList());
};

const fetchAlgoliaProductsForVariantLinks = async (cards, abortController) => {
  const styleNumbers = [];
  const productIds = [];

  cards.forEach((card) => {
    const styleNo =
      card.getAttribute('data-style-no') ||
      card.getAttribute('data-merch-style-number') ||
      '';
    const productId = card.getAttribute('data-product-id');

    if (styleNo && !styleNumbers.includes(styleNo)) {
      styleNumbers.push(styleNo);
    }
    if (productId && !productIds.includes(productId)) {
      productIds.push(productId);
    }
  });

  const productsByHandle = new Map();
  const productsById = new Map();

  if (styleNumbers.length && window.GraphQLUtils?.fetchAllProductDetails) {
    try {
      const productsByStyle = await window.GraphQLUtils.fetchAllProductDetails(
        styleNumbers,
        Shopify.shop,
        abortController
      );

      Object.values(productsByStyle)
        .flat()
        .forEach((product) => {
          if (product?.handle) {
            productsByHandle.set(product.handle, product);
          }
          if (product?.id) {
            productsById.set(
              String(product.id).replace('gid://shopify/Product/', ''),
              product
            );
            productsById.set(product.id, product);
          }
        });
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.error('Algolia variant link style fetch failed', error);
      }
    }
  }

  const missingIds = productIds.filter(
    (productId) => !productsById.has(String(productId))
  );

  if (missingIds.length) {
    try {
      const products = await fetchAlgoliaProductsByIds(missingIds, abortController);
      products.forEach((product) => {
        if (product?.handle) {
          productsByHandle.set(product.handle, product);
        }
        if (product?.id) {
          productsById.set(
            String(product.id).replace('gid://shopify/Product/', ''),
            product
          );
          productsById.set(product.id, product);
        }
      });
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.error('Algolia variant link product fetch failed', error);
      }
    }
  }

  mergeAlgoliaVariantLinkProductCache(productsByHandle, productsById);
  return { productsByHandle, productsById };
};

const setupAlgoliaFilteredVariantLinks = () => {
  let linkTimer = null;
  let linkAbort = null;
  let linkGeneration = 0;

  const refreshAlgoliaFilteredVariantLinks = async () => {
    const cards = [
      ...document.querySelectorAll(
        '#algolia-shopify-instantsearch product-card[data-source-file="algolia-instant-search"]'
      ),
    ];

    if (!cards.length) {
      return;
    }

    const refinementList = getAlgoliaActiveRefinementList();
    const cardsNeedingLinks = cards.filter((card) => {
      const required = getAlgoliaRequiredVariantOptions(
        getAlgoliaHitContextFromCard(card),
        refinementList
      );
      return required.length > 0;
    });

    if (!cardsNeedingLinks.length) {
      cards.forEach((card) => clearAlgoliaFilteredVariantLinks(card));
      return;
    }

    if (linkAbort) {
      linkAbort.abort();
    }
    linkAbort = new AbortController();
    const controller = linkAbort;
    const generation = ++linkGeneration;

    const { productsByHandle, productsById } = await fetchAlgoliaProductsForVariantLinks(
      cardsNeedingLinks,
      controller
    );

    if (controller.signal.aborted || generation !== linkGeneration) {
      return;
    }

    cardsNeedingLinks.forEach((productCard) => {
      applyAlgoliaFilteredVariantLinksToCard(
        productCard,
        productsByHandle,
        refinementList
      );
    });
  };

  const scheduleAlgoliaFilteredVariantLinks = () => {
    if (linkTimer) {
      clearTimeout(linkTimer);
    }

    linkTimer = setTimeout(() => {
      linkTimer = null;
      refreshAlgoliaFilteredVariantLinks();
    }, 250);
  };

  document.addEventListener('algolia.instantSearch.onRender', scheduleAlgoliaFilteredVariantLinks);
  scheduleAlgoliaFilteredVariantLinks();
};

const getAlgoliaInStockProducts = (products) =>
  (products || []).filter((product) => product && product.totalInventory > 0);

const getAlgoliaProductSalePrice = (product) => {
  const amount = product?.priceRange?.minVariantPrice?.amount;
  const parsed = parseFloat(amount);
  return Number.isFinite(parsed) ? parsed : null;
};

const getAlgoliaVariantSalePairs = (product) =>
  (product?.variants?.edges || [])
    .map((edge) => edge?.node)
    .filter(Boolean)
    .map((node) => {
      const price = parseFloat(node.price?.amount);
      const compare = parseFloat(node.compareAtPrice?.amount);
      if (!Number.isFinite(price) || !Number.isFinite(compare) || compare <= price) {
        return null;
      }
      return { price, compare };
    })
    .filter(Boolean);

const getAlgoliaProductCompareAt = (product, salePrice) => {
  const pairs = getAlgoliaVariantSalePairs(product);
  if (!pairs.length || salePrice == null) {
    return undefined;
  }

  // Prefer compare-at from variants at the sale min; otherwise any on-sale variant.
  const atSaleMin = pairs.filter((pair) => Math.abs(pair.price - salePrice) < 0.005);
  const pool = atSaleMin.length ? atSaleMin : pairs;
  const compareAt = Math.max(...pool.map((pair) => pair.compare));
  return compareAt > salePrice ? compareAt : undefined;
};

const getAlgoliaStylePriceBounds = (products) => {
  const inStock = getAlgoliaInStockProducts(products);
  if (!inStock.length) {
    return null;
  }

  const mins = inStock
    .map((product) => parseFloat(product.priceRange?.minVariantPrice?.amount))
    .filter((amount) => Number.isFinite(amount));
  const maxs = inStock
    .map((product) => parseFloat(product.priceRange?.maxVariantPrice?.amount))
    .filter((amount) => Number.isFinite(amount));

  if (!mins.length || !maxs.length) {
    return null;
  }

  return {
    min: Math.min(...mins),
    max: Math.max(...maxs),
  };
};

const getAlgoliaStyleCompareAt = (products, saleMin) => {
  const pairs = getAlgoliaInStockProducts(products).flatMap((product) =>
    getAlgoliaVariantSalePairs(product)
  );

  if (!pairs.length || saleMin == null) {
    return undefined;
  }

  const atSaleMin = pairs.filter((pair) => Math.abs(pair.price - saleMin) < 0.005);
  const pool = atSaleMin.length ? atSaleMin : pairs;
  const compareAt = Math.max(...pool.map((pair) => pair.compare));
  return compareAt > saleMin ? compareAt : undefined;
};

const applyAlgoliaMarketPriceMarkup = (priceRoot, min, max, compareAt) => {
  if (!priceRoot || min == null || max == null) {
    return;
  }

  const isPriceRange = min !== max;
  const displayMin = teqFormatPriceFromRaw(min);
  const displayMax = teqFormatPriceFromRaw(max);
  const saleText =
    isPriceRange && displayMin && displayMax
      ? `${displayMin} – ${displayMax}`
      : displayMax || displayMin;

  const priceCents = teqNormalizePriceToCents(min);
  const compareCents =
    compareAt != null ? teqNormalizePriceToCents(compareAt) : null;
  // Keep strikethrough whenever the sale min is discounted, including ranges.
  const showCompare =
    compareCents != null &&
    priceCents != null &&
    compareCents > priceCents;

  priceRoot.classList.toggle('has-price-range', isPriceRange);

  let delEl = priceRoot.querySelector('del');
  let insEl = priceRoot.querySelector('ins');

  if (!insEl) {
    insEl = document.createElement('ins');
    insEl.innerHTML = '<span class="amount"></span>';
    priceRoot.appendChild(insEl);
  }

  const amountEl = insEl.querySelector('.amount') || insEl;
  amountEl.textContent = saleText;

  if (showCompare) {
    const compareFormatted = teqFormatPriceFromRaw(compareAt);
    if (!delEl) {
      delEl = document.createElement('del');
      delEl.innerHTML = '<span class="amount"></span>';
      priceRoot.insertBefore(delEl, insEl);
    }
    const delAmount = delEl.querySelector('.amount') || delEl;
    delAmount.textContent = compareFormatted;
  } else if (delEl) {
    delEl.remove();
  }
};

const fetchAlgoliaProductsByIds = async (productIds, abortController) => {
  if (!productIds.length) {
    return [];
  }

  const storefrontAccessToken =
    window.storefrontAccessToken ||
    (typeof window.waitForStorefrontToken === 'function'
      ? await window.waitForStorefrontToken(2500).catch(() => null)
      : null);

  if (!storefrontAccessToken || typeof Shopify === 'undefined') {
    return [];
  }

  const shopUrl = Shopify.shop;
  const url = `https://${shopUrl}/api/2024-10/graphql.json`;
  const gids = productIds.map((id) =>
    String(id).startsWith('gid://') ? id : `gid://shopify/Product/${id}`
  );

  const chunkSize = 50;
  const allProducts = [];

  for (let index = 0; index < gids.length; index += chunkSize) {
    const ids = gids.slice(index, index + chunkSize);
    const query = `
    query AlgoliaProductsByIds($ids: [ID!]!, $country: CountryCode) @inContext(country: $country) {
      nodes(ids: $ids) {
        ... on Product {
          id
          handle
          title
          totalInventory
          priceRange {
            minVariantPrice { amount currencyCode }
            maxVariantPrice { amount currencyCode }
          }
          variants(first: 100) {
            edges {
              node {
                id
                quantityAvailable
                compareAtPrice { amount currencyCode }
                price { amount currencyCode }
                selectedOptions {
                  name
                  value
                }
              }
            }
          }
        }
      }
    }
  `;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
      },
      body: JSON.stringify({
        query,
        variables: {
          ids,
          country: Shopify.country,
        },
      }),
      signal: abortController?.signal,
    });

    const payload = await response.json();
    if (payload?.errors) {
      console.error('Algolia price hydration GraphQL errors:', payload.errors);
      continue;
    }

    allProducts.push(...(payload?.data?.nodes || []).filter(Boolean));
  }

  return allProducts;
};

const setupAlgoliaMarketPriceHydration = () => {
  let hydrateTimer = null;
  let hydrateAbort = null;
  let hydrateGeneration = 0;

  const hydrateAlgoliaMarketPrices = async () => {
    const cards = [
      ...document.querySelectorAll(
        '#algolia-shopify-instantsearch product-card[data-source-file="algolia-instant-search"]'
      ),
    ];

    if (!cards.length) {
      return;
    }

    const styleNumbers = [];
    const orphanProductIds = [];

    cards.forEach((card) => {
      const styleNo =
        card.getAttribute('data-style-no') ||
        card.getAttribute('data-merch-style-number') ||
        '';
      const productId = card.getAttribute('data-product-id');

      if (styleNo && !styleNumbers.includes(styleNo)) {
        styleNumbers.push(styleNo);
      }
      if (productId && !orphanProductIds.includes(productId)) {
        orphanProductIds.push(productId);
      }
    });

    if (!styleNumbers.length && !orphanProductIds.length) {
      return;
    }

    if (hydrateAbort) {
      hydrateAbort.abort();
    }
    hydrateAbort = new AbortController();
    const controller = hydrateAbort;
    const generation = ++hydrateGeneration;

    let productsByStyle = {};
    try {
      if (styleNumbers.length && window.GraphQLUtils?.fetchAllProductDetails) {
        productsByStyle = await window.GraphQLUtils.fetchAllProductDetails(
          styleNumbers,
          Shopify.shop,
          controller
        );
      }
    } catch (error) {
      if (error?.name === 'AbortError') {
        return;
      }
      console.error('Algolia style price hydration failed', error);
    }

    if (controller.signal.aborted || generation !== hydrateGeneration) {
      return;
    }

    let orphanProducts = [];
    try {
      orphanProducts = await fetchAlgoliaProductsByIds(orphanProductIds, controller);
    } catch (error) {
      if (error?.name === 'AbortError') {
        return;
      }
      console.error('Algolia product price hydration failed', error);
    }

    if (controller.signal.aborted || generation !== hydrateGeneration) {
      return;
    }

    const productsByHandle = new Map();
    const productsById = new Map();

    Object.values(productsByStyle)
      .flat()
      .forEach((product) => {
        if (product?.handle) {
          productsByHandle.set(product.handle, product);
        }
        if (product?.id) {
          productsById.set(String(product.id).replace('gid://shopify/Product/', ''), product);
          productsById.set(product.id, product);
        }
      });

    orphanProducts.forEach((product) => {
      if (product?.handle) {
        productsByHandle.set(product.handle, product);
      }
      if (product?.id) {
        productsById.set(String(product.id).replace('gid://shopify/Product/', ''), product);
        productsById.set(product.id, product);
      }
    });

    cards.forEach((productCard) => {
      const styleNo =
        productCard.getAttribute('data-style-no') ||
        productCard.getAttribute('data-merch-style-number') ||
        '';
      const productId = productCard.getAttribute('data-product-id');
      const styleProducts = styleNo ? productsByStyle[styleNo] || [] : [];
      const productForLinks = resolveAlgoliaProductForCard(
        productCard,
        productsByHandle,
        productsById
      );
      const fallbackProduct =
        productForLinks ||
        productsById.get(String(productId || '')) ||
        productsByHandle.get(
          getAlgoliaProductHandleFromUrl(
            productCard.querySelector('.product-featured-image-link')?.getAttribute('href')
          )
        );
      const productsForCard =
        styleProducts.length > 0
          ? styleProducts
          : fallbackProduct
            ? [fallbackProduct]
            : [];

      if (!productsForCard.length) {
        if (productForLinks) {
          applyAlgoliaFilteredVariantLinks(
            productCard,
            productForLinks,
            getAlgoliaHitContextFromCard(productCard),
            getAlgoliaRequiredVariantOptions(
              getAlgoliaHitContextFromCard(productCard),
              getAlgoliaActiveRefinementList()
            ),
            productsByHandle
          );
        }
        productCard.classList.remove('hide-price');
        return;
      }

      // Visible card price should follow the hit's product, not the whole style
      // group — style-wide min/max was turning sale cards into ranges and
      // dropping the compare-at strikethrough after hydration.
      const displayProduct = fallbackProduct || productsForCard[0];
      const pricingProducts = displayProduct ? [displayProduct] : productsForCard;

      const currencyCode =
        displayProduct?.priceRange?.minVariantPrice?.currencyCode ||
        productsForCard[0]?.priceRange?.minVariantPrice?.currencyCode ||
        getAlgoliaActiveCurrencyCode();

      if (window.GraphQLUtils?.updateProductCardCurrencyMetadata) {
        window.GraphQLUtils.updateProductCardCurrencyMetadata(productCard, currencyCode);
      } else {
        productCard.dataset.currencyCode = currencyCode;
        const priceRoot = productCard.querySelector('.productInfoRight .price');
        if (priceRoot) {
          priceRoot.dataset.currencyCode = currencyCode;
        }
      }

      const bounds = getAlgoliaStylePriceBounds(pricingProducts);
      if (bounds) {
        const compareAt = getAlgoliaStyleCompareAt(pricingProducts, bounds.min);
        const priceRoot = productCard.querySelector('.productInfoRight .price');
        applyAlgoliaMarketPriceMarkup(priceRoot, bounds.min, bounds.max, compareAt);
        productCard.dataset.defaultPrice = String(bounds.min);
        productCard.dataset.defaultCompareAtPrice =
          compareAt != null ? String(compareAt) : '';
      }

      productCard
        .querySelectorAll('.product-card-swatch[data-href]')
        .forEach((swatch) => {
          const handle = getAlgoliaProductHandleFromUrl(swatch.getAttribute('data-href'));
          const swatchProduct = productsByHandle.get(handle);
          if (!swatchProduct) {
            return;
          }

          const salePrice = getAlgoliaProductSalePrice(swatchProduct);
          if (salePrice == null) {
            return;
          }

          const swatchCompareAt = getAlgoliaProductCompareAt(swatchProduct, salePrice);
          const swatchCurrencyCode =
            swatchProduct?.priceRange?.minVariantPrice?.currencyCode || currencyCode;
          swatch.setAttribute('data-price', String(salePrice));
          swatch.setAttribute(
            'data-compare-at-price',
            swatchCompareAt != null ? String(swatchCompareAt) : ''
          );
          swatch.setAttribute('data-currency-code', swatchCurrencyCode);
        });

      delete productCard.dataset.defaultPriceCaptured;
      delete productCard.dataset.defaultPriceLabel;
      delete productCard.dataset.defaultCompareLabel;
      bindAlgoliaProductCardSwatches(productCard);

      if (productForLinks) {
        applyAlgoliaFilteredVariantLinks(
          productCard,
          productForLinks,
          getAlgoliaHitContextFromCard(productCard),
          getAlgoliaRequiredVariantOptions(
            getAlgoliaHitContextFromCard(productCard),
            getAlgoliaActiveRefinementList()
          ),
          productsByHandle
        );
      }

      productCard.classList.remove('hide-price');
    });
  };

  const scheduleAlgoliaMarketPriceHydration = () => {
    if (hydrateTimer) {
      clearTimeout(hydrateTimer);
    }
    hydrateTimer = setTimeout(() => {
      hydrateTimer = null;
      hydrateAlgoliaMarketPrices();
    }, 120);
  };

  document.addEventListener('algolia.instantSearch.onRender', scheduleAlgoliaMarketPriceHydration);
};

const setupAlgoliaHitsReadyGate = () => {
  let readyTimer = null;

  const markReady = () => {
    const root = document.querySelector('#algolia-shopify-instantsearch');
    if (!root || root.classList.contains('algolia-hits-ready')) {
      return;
    }

    root.classList.add('algolia-hits-ready');
    document.documentElement.classList.add('algolia-plp-ready');
  };

  const scheduleMarkReady = () => {
    if (readyTimer) {
      clearTimeout(readyTimer);
    }

    // Wait for facet count patches, variant links, and market price hydration.
    readyTimer = setTimeout(() => {
      readyTimer = null;
      markReady();
    }, 360);
  };

  document.addEventListener('algolia.instantSearch.onRender', scheduleMarkReady);
  document.addEventListener('algolia.instantsearch.afterInit', scheduleMarkReady);
};

const setupAlgoliaColorFacetSwatches = () => {
  const getColorMap = () => {
    if (window.NatoriColorSwatches?.getColorSwatchLookup) {
      const { map } = window.NatoriColorSwatches.getColorSwatchLookup();
      const result = new Map();
      Object.keys(map || {}).forEach((key) => {
        const row = map[key];
        const val = row?.val || row?.image || row?.color || '';
        if (key && val) result.set(key, val);
      });
      return result;
    }

    const configEl = document.getElementById('algolia-color-swatches-config');
    if (!configEl) {
      return null;
    }
    try {
      const rows = JSON.parse(configEl.textContent || '[]');
      const map = new Map();
      rows.forEach((row) => {
        if (row?.key) {
          const key = String(row.key).toLowerCase().trim();
          // Match Liquid facets: first definition wins (settings list has many duplicates).
          if (key && !map.has(key)) {
            map.set(key, row.val || '');
          }
        }
      });
      return map;
    } catch (error) {
      return null;
    }
  };

  // Algolia Shopify color facet labels are often bare text + count span
  // (no .ais-RefinementList-labelText). label.textContent becomes "Beige17".
  const getColorFacetLabelText = (item, label) => {
    const labelTextEl = item.querySelector('.ais-RefinementList-labelText');
    if (labelTextEl) {
      return labelTextEl.textContent?.trim() || '';
    }

    const clone = label.cloneNode(true);
    clone
      .querySelectorAll(
        '.ais-RefinementList-count, .ais-facet--count-distinct, .ais-RefinementList-checkbox, input'
      )
      .forEach((el) => el.remove());
    return clone.textContent?.trim() || '';
  };

  const normalizeColorValue = (value) =>
    formatAlgoliaColorFilterLabel(String(value || ''))
      .toLowerCase()
      .trim();

  /**
   * Derive refined color values from InstantSearch uiState / active chips
   * (Shopify Algolia color facets often have no checkbox inputs).
   */
  const getRefinedColorValues = () => {
    const refined = new Set();

    const addValue = (value) => {
      const cleaned = normalizeColorValue(value);
      if (cleaned) refined.add(cleaned);
      // Also keep a decoded raw form for JSON-array facet values like ["Beige"].
      try {
        const decoded = decodeURIComponent(String(value || ''));
        const cleanedDecoded = normalizeColorValue(decoded);
        if (cleanedDecoded) refined.add(cleanedDecoded);
      } catch (error) {
        // ignore
      }
    };

    try {
      const instantsearch =
        window.algoliaShopify?.instantsearch?.search ||
        window.algoliaShopify?.instantsearch;
      const uiState =
        typeof instantsearch?.getUiState === 'function'
          ? instantsearch.getUiState()
          : null;
      if (uiState) {
        Object.keys(uiState).forEach((indexId) => {
          const list = uiState[indexId]?.refinementList || {};
          Object.keys(list).forEach((attribute) => {
            const attr = String(attribute).toLowerCase();
            if (
              !attr.includes('color') &&
              !attr.includes('simple_color')
            ) {
              return;
            }
            (list[attribute] || []).forEach((value) => addValue(value));
          });
        });
      }

      const helperState = instantsearch?.helper?.state;
      const disjunctive = helperState?.disjunctiveFacetsRefinements || {};
      Object.keys(disjunctive).forEach((attribute) => {
        const attr = String(attribute).toLowerCase();
        if (!attr.includes('color') && !attr.includes('simple_color')) {
          return;
        }
        (disjunctive[attribute] || []).forEach((value) => addValue(value));
      });
    } catch (error) {
      // Fall through to chip / URL parsing.
    }

    document
      .querySelectorAll(
        '#algolia-shopify-instantsearch .ais-current-refined-values--link[data-attribute*="color"], #algolia-shopify-instantsearch .ais-current-refined-values--link[data-attribute*="simple_color"]'
      )
      .forEach((link) => {
        addValue(link.getAttribute('data-value'));
        addValue(link.getAttribute('data-label'));
      });

    return refined;
  };

  let colorMap = null;

  const applyFillToLabel = (label, fill) => {
    if (!fill) return;
    const fillStr = String(fill);
    if (fillStr.includes('gradient(')) {
      label.style.setProperty('--algolia-swatch-img', fillStr);
      label.style.setProperty('--algolia-swatch-fill', '#e8e8e8');
    } else if (fillStr.includes('http') || fillStr.includes('/') || fillStr.includes('.')) {
      const bgCss =
        window.NatoriColorSwatches?.swatchBackgroundImageCss?.(fillStr, window.location.hostname) ||
        `--option-color-image: url('${fillStr}');`;
      const urlMatch = bgCss.match(/url\('([^']+)'\)/);
      if (urlMatch) {
        label.style.setProperty('--algolia-swatch-img', `url('${urlMatch[1]}')`);
        label.style.setProperty('--algolia-swatch-fill', '#e8e8e8');
      } else {
        label.style.setProperty('--algolia-swatch-fill', fillStr);
        label.style.removeProperty('--algolia-swatch-img');
      }
    } else {
      label.style.setProperty('--algolia-swatch-fill', fillStr);
      label.style.removeProperty('--algolia-swatch-img');
    }
  };

  const enhance = () => {
    colorMap = getColorMap() || new Map();
    const refinedColors = getRefinedColorValues();

    const roots = document.querySelectorAll(
      [
        '#algolia-shopify-instantsearch [class*="options.color"]',
        '#algolia-shopify-instantsearch [class*="simple_color"]',
        '#algolia-shopify-instantsearch .teq-facet--color',
        '#algolia-shopify-instantsearch .teq-facet--optionscolor',
        '#algolia-shopify-instantsearch .teq-facet--metacustomsimple_color',
      ].join(',')
    );

    roots.forEach((root) => {
      root.classList.add('algolia-color-facet');
      root.querySelectorAll('.ais-RefinementList-item').forEach((item) => {
        const label = item.querySelector('.ais-RefinementList-label');
        if (!label) {
          return;
        }

        label.classList.add('algolia-color-swatch');

        const valueText = getColorFacetLabelText(item, label);
        const cleaned = normalizeColorValue(valueText);
        const checkbox = item.querySelector(
          '.ais-RefinementList-checkbox, input[type="checkbox"]:not(.ais-dropdown-checkbox)'
        );
        const checkboxValue = normalizeColorValue(
          checkbox?.value && checkbox.value !== 'on'
            ? checkbox.value
            : ''
        );

        // Always clear stale selected state, then derive from InstantSearch /
        // chips — never from list index or accordion dropdown checkboxes.
        item.classList.remove('ais-RefinementList-item--selected');
        let isRefined = false;
        if (refinedColors.size) {
          isRefined =
            refinedColors.has(cleaned) ||
            (checkboxValue ? refinedColors.has(checkboxValue) : false);
        } else if (checkbox) {
          isRefined = Boolean(
            checkbox.checked || checkbox.getAttribute('aria-checked') === 'true'
          );
        }
        item.classList.toggle('ais-RefinementList-item--selected', isRefined);
        if (checkbox && checkbox.value !== 'on' && refinedColors.size) {
          checkbox.checked = isRefined;
        }

        const fill =
          window.NatoriColorSwatches?.resolveFill?.(cleaned) ||
          colorMap.get(cleaned) ||
          null;
        applyFillToLabel(label, fill);
      });
    });
  };

  document.addEventListener('algolia.instantSearch.onRender', () => {
    setTimeout(enhance, 30);
  });

  document.addEventListener('natori:color-swatches-hydrated', () => {
    colorMap = null;
    enhance();
  });

  // Ensure Storefront hydration runs and re-paints facets when complete.
  if (window.NatoriColorSwatches?.hydrateFromStorefront) {
    window.NatoriColorSwatches.hydrateFromStorefront().then(() => {
      colorMap = null;
      enhance();
    });
  }
};

const setupAlgoliaMobileFilterDrawer = () => {
  const ROOT_ID = 'algolia-shopify-instantsearch';
  const FILTER_BY_LABEL = "Filter:";
  const SHOW_FILTERS_LABEL = 'Show filters';
  let isDrawerOpen = false;
  let savedScrollY = 0;

  const getRoot = () => document.getElementById(ROOT_ID);
  const getFacets = () => getRoot()?.querySelector('.ais-facets');

  const pinShowFiltersLabel = () => {
    document
      .querySelectorAll(`#${ROOT_ID} .ais-facets-button`)
      .forEach((button) => {
        if (button.textContent.trim() !== SHOW_FILTERS_LABEL) {
          button.textContent = SHOW_FILTERS_LABEL;
        }
        button.setAttribute('aria-expanded', isDrawerOpen ? 'true' : 'false');
        button.setAttribute('aria-haspopup', 'dialog');
      });
  };

  const ensureDrawerChrome = (facets) => {
    if (!facets || facets.querySelector('.algolia-facets-drawer-header')) {
      return;
    }

    const header = document.createElement('div');
    header.className = 'algolia-facets-drawer-header';
    header.innerHTML = `
      <span class="h4 filter-label">Filter</span>
      <button type="button" class="algolia-facets-drawer-close side-panel-close" aria-label="Close filters">
        <svg class="icon icon-close" viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">
          <path d="M4.222 4.222a1 1 0 0 1 1.414 0L10 8.586l4.364-4.364a1 1 0 0 1 1.414 1.414L11.414 10l4.364 4.364a1 1 0 1 1-1.414 1.414L10 11.414l-4.364 4.364a1 1 0 1 1-1.414-1.414L8.586 10 4.222 5.636a1 1 0 0 1 0-1.414z" fill="currentColor"/>
        </svg>
      </button>
 
    `;
    facets.insertBefore(header, facets.firstChild);
  };

  const ensureDesktopFilterLabel = (facets) => {
    if (!facets || facets.querySelector('.algolia-facets-label')) {
      return;
    }

    const label = document.createElement('label');
    label.className = 'facets__label algolia-facets-label';
    label.textContent = FILTER_BY_LABEL;

    const drawerHeader = facets.querySelector('.algolia-facets-drawer-header');
    if (drawerHeader && drawerHeader.nextSibling) {
      facets.insertBefore(label, drawerHeader.nextSibling);
    } else if (drawerHeader) {
      facets.appendChild(label);
    } else {
      facets.insertBefore(label, facets.firstChild);
    }
  };

  const getActiveFacetsState = (facets) => {
    const chips = facets?.querySelector('.ais-current-refined-values-container');
    const clear = facets?.querySelector('.ais-clear-refinements-container');
    const clearButton = clear?.querySelector(
      '.ais-ClearRefinements-button, .ais-clear-refinements--body, button'
    );
    const hasChips = Boolean(
      chips?.querySelector(
        '.ais-current-refined-values--link, .ais-CurrentRefinements-category, .ais-CurrentRefinements-item'
      )
    );
    const clearDisabled =
      !clearButton ||
      clearButton.disabled ||
      clearButton.hasAttribute('disabled') ||
      clearButton.classList.contains('ais-ClearRefinements-button--disabled') ||
      clearButton.getAttribute('aria-disabled') === 'true' ||
      Boolean(clear?.querySelector('.ais-Panel--noRefinement'));

    return { chips, clear, clearButton, hasChips, clearDisabled };
  };

  const restoreDesktopActiveFacetsOrder = (facets) => {
    const placeholder = facets.querySelector('.algolia-facets-chips-placeholder');
    const { chips, clear } = getActiveFacetsState(facets);
    const drawerActive = facets.querySelector('.algolia-facets-drawer-active-facets');

    if (drawerActive) {
      drawerActive.hidden = true;
    }

    if (!placeholder) {
      return;
    }

    if (chips && chips.parentElement !== placeholder.parentElement) {
      placeholder.insertAdjacentElement('afterend', chips);
    }

    if (clear) {
      const chipsAnchor = chips || placeholder;
      if (clear.previousElementSibling !== chipsAnchor) {
        chipsAnchor.insertAdjacentElement('afterend', clear);
      }
    }
  };

  /**
   * Show live applied chips + Clear all inside the mobile filter drawer.
   */
  const ensureDrawerActiveFacets = (facets) => {
    if (!facets) {
      return;
    }

    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    if (!isMobile) {
      restoreDesktopActiveFacetsOrder(facets);
      return;
    }

    let placeholder = facets.querySelector('.algolia-facets-chips-placeholder');
    if (!placeholder) {
      placeholder = document.createElement('div');
      placeholder.className = 'algolia-facets-chips-placeholder';
      placeholder.hidden = true;
      facets.insertBefore(placeholder, facets.firstChild);
    }

    const header = facets.querySelector('.algolia-facets-drawer-header');
    let drawerActive = facets.querySelector('.algolia-facets-drawer-active-facets');
    if (!drawerActive && header) {
      drawerActive = document.createElement('div');
      drawerActive.className = 'algolia-facets-drawer-active-facets';
      header.insertAdjacentElement('afterend', drawerActive);
    }

    if (!drawerActive) {
      return;
    }

    const { chips, clear, hasChips, clearDisabled } = getActiveFacetsState(facets);
    drawerActive.hidden = !hasChips && clearDisabled;

    if (hasChips && chips) {
      chips.removeAttribute('hidden');
      chips.style.removeProperty('display');
      drawerActive.appendChild(chips);
    }

    if ((hasChips || !clearDisabled) && clear) {
      clear.removeAttribute('hidden');
      clear.style.removeProperty('display');
      drawerActive.appendChild(clear);
    }
  };

  /**
   * Keep applied chips + Clear all visible on the mobile PLP (outside the drawer).
   * Mirror InstantSearch widgets into a dedicated slot — clicks still hit the
   * live widget controls via event forwarding when needed.
   */
  const ensureMobileActiveFacetsSlot = () => {
    const root = getRoot();
    const facets = getFacets();
    if (!root || !facets) {
      return;
    }

    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    let slot = root.querySelector('.algolia-mobile-active-facets');

    if (!isMobile) {
      ensureDrawerActiveFacets(facets);
      if (slot) {
        slot.hidden = true;
        slot.replaceChildren();
      }
      return;
    }

    if (!slot) {
      slot = document.createElement('div');
      slot.className = 'algolia-mobile-active-facets';
    }

    // Re-home if InstantSearch re-rendered and detached the slot, or on first create.
    // Never use root.insertBefore(slot, facets) — .ais-facets is often nested, not a
    // direct child of #algolia-shopify-instantsearch (throws NotFoundError).
    if (!slot.isConnected || !root.contains(slot)) {
      const toolbar = root.querySelector('.algolia-mobile-toolbar');
      if (toolbar?.parentNode) {
        toolbar.parentNode.insertBefore(slot, toolbar.nextSibling);
      } else if (facets.parentNode) {
        facets.parentNode.insertBefore(slot, facets);
      } else {
        root.appendChild(slot);
      }
    }

    ensureDrawerActiveFacets(facets);

    const { chips, clear, clearButton, hasChips, clearDisabled } =
      getActiveFacetsState(facets);

    slot.replaceChildren();

    if (!hasChips && clearDisabled) {
      slot.hidden = true;
      return;
    }

    slot.hidden = false;

    if (hasChips && chips) {
      const chipsClone = chips.cloneNode(true);
      chipsClone.classList.add('algolia-mobile-active-facets__chips');
      chipsClone.removeAttribute('hidden');
      chipsClone.querySelectorAll('[hidden]').forEach((el) => el.removeAttribute('hidden'));
      // Forward chip clicks to the live InstantSearch widget (same order).
      chipsClone.addEventListener('click', (event) => {
        const cloneLink = event.target.closest(
          '.ais-current-refined-values--link, .ais-CurrentRefinements-delete, a, button'
        );
        if (!cloneLink) {
          return;
        }
        event.preventDefault();
        const cloneLinks = [
          ...chipsClone.querySelectorAll(
            '.ais-current-refined-values--link, .ais-CurrentRefinements-delete'
          ),
        ];
        const liveLinks = [
          ...chips.querySelectorAll(
            '.ais-current-refined-values--link, .ais-CurrentRefinements-delete'
          ),
        ];
        const index = cloneLinks.indexOf(cloneLink);
        const live = index > -1 ? liveLinks[index] : null;
        live?.click();
      });
      slot.appendChild(chipsClone);
    }

    // Always show Clear all next to outside chips when any refinement is active.
    if (hasChips || !clearDisabled) {
      const clearClone = document.createElement('button');
      clearClone.type = 'button';
      clearClone.className = 'algolia-mobile-active-facets__clear';
      clearClone.textContent =
        (clearButton?.textContent || '').trim() || 'Clear all';
      clearClone.addEventListener('click', (event) => {
        event.preventDefault();
        if (clearButton && !clearDisabled) {
          clearButton.click();
          return;
        }
        // Fallback: remove each live chip if Clear widget is briefly unavailable.
        chips
          ?.querySelectorAll(
            '.ais-current-refined-values--link, .ais-CurrentRefinements-delete'
          )
          .forEach((link) => link.click());
      });
      slot.appendChild(clearClone);
    }
  };

  const ensureFacetsBackdrop = () => {
    let backdrop = document.querySelector('.algolia-facets-backdrop');
    if (!backdrop) {
      backdrop = document.createElement('div');
      backdrop.className = 'algolia-facets-backdrop';
      backdrop.setAttribute('aria-hidden', 'true');
      document.body.appendChild(backdrop);
      // Clicks bubble to the capture handler below — no second close call here.
    }
    return backdrop;
  };

  let drawerCloseTimer = null;
  let isDrawerClosing = false;

  const finishDrawerClose = (facets) => {
    isDrawerClosing = false;
    facets?.classList.remove('algolia-facets-drawer--closing');
    document.body.classList.remove('algolia-facets-scroll-lock');
    document.body.style.top = '';
    window.scrollTo(0, savedScrollY);
  };

  const setDrawerOpen = (open) => {
    const root = getRoot();
    const facets = getFacets();
    if (!root || !facets) {
      return;
    }

    ensureDrawerChrome(facets);
    ensureDesktopFilterLabel(facets);
    facets.classList.add('algolia-facets-drawer');
    facets.style.removeProperty('display');

    const backdrop = ensureFacetsBackdrop();

    if (drawerCloseTimer) {
      clearTimeout(drawerCloseTimer);
      drawerCloseTimer = null;
    }

    if (open) {
      isDrawerClosing = false;
      facets.classList.remove('algolia-facets-drawer--closing');
      // Force layout so opening from translateX(100%) always animates.
      void facets.offsetWidth;
      facets.classList.add('algolia-facets-drawer--open');
      backdrop.classList.add('is-visible');
      backdrop.setAttribute('aria-hidden', 'false');
      document.body.classList.add('open-cc', 'algolia-facets-open');

      if (!isDrawerOpen) {
        savedScrollY = window.scrollY || window.pageYOffset || 0;
        document.body.style.top = `-${savedScrollY}px`;
        document.body.classList.add('algolia-facets-scroll-lock');
      }

      isDrawerOpen = true;
      ensureDrawerActiveFacets(facets);
      pinShowFiltersLabel();
      return;
    }

    // Already closed / mid-close — avoid stripping --closing in the same tick
    // (backdrop click + capture handler can both fire).
    if (
      isDrawerClosing ||
      (!isDrawerOpen && !facets.classList.contains('algolia-facets-drawer--open'))
    ) {
      return;
    }

    isDrawerClosing = true;
    isDrawerOpen = false;
    facets.classList.remove('algolia-facets-drawer--open');
    facets.classList.add('algolia-facets-drawer--closing');
    // Re-enable transform transition from the open position.
    void facets.offsetWidth;
    backdrop.classList.remove('is-visible');
    backdrop.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('open-cc', 'algolia-facets-open');
    pinShowFiltersLabel();

    const onTransitionEnd = (event) => {
      if (event.target !== facets || event.propertyName !== 'transform') {
        return;
      }
      facets.removeEventListener('transitionend', onTransitionEnd);
      finishDrawerClose(facets);
    };

    facets.addEventListener('transitionend', onTransitionEnd);
    drawerCloseTimer = setTimeout(() => {
      facets.removeEventListener('transitionend', onTransitionEnd);
      finishDrawerClose(facets);
      drawerCloseTimer = null;
    }, 350);
  };

  const eventPathContains = (event, selector) => {
    const path =
      typeof event.composedPath === 'function' ? event.composedPath() : [];
    if (path.length) {
      return path.some(
        (node) => node instanceof Element && node.matches?.(selector)
      );
    }
    // Fallback when composedPath is unavailable / target detached mid-render.
    return Boolean(
      event.target instanceof Element && event.target.closest(selector)
    );
  };

  document.addEventListener('click', (event) => {
    const root = getRoot();
    if (!root) {
      return;
    }

    if (eventPathContains(event, '.ais-facets-button')) {
      const toggle = event.target.closest?.('.ais-facets-button');
      if (toggle && root.contains(toggle)) {
        // Toggle our drawer independently of Algolia's display:none toggling.
        event.preventDefault();
        event.stopPropagation();
        setDrawerOpen(!isDrawerOpen);
        pinShowFiltersLabel();
        return;
      }
    }

    if (eventPathContains(event, '.algolia-facets-drawer-close')) {
      event.preventDefault();
      setDrawerOpen(false);
      return;
    }

    // Backdrop / empty space left of drawer closes filters.
    if (isDrawerOpen) {
      const insideDrawer = Boolean(
        event.target instanceof Element &&
          event.target.closest(`#${ROOT_ID} .ais-facets`)
      );
      const onToggle = eventPathContains(event, '.ais-facets-button');
      const onBackdrop = eventPathContains(
        event,
        '.algolia-facets-backdrop, .click-capture'
      );

      if ((onBackdrop || !insideDrawer) && !onToggle) {
        setDrawerOpen(false);
      }
    }
  }, true);

  document.addEventListener('algolia.instantSearch.onRender', () => {
    const facets = getFacets();
    if (facets) {
      ensureDrawerChrome(facets);
      ensureDesktopFilterLabel(facets);
      facets.classList.add('algolia-facets-drawer');
      // Persist open state across InstantSearch DOM replacement.
      if (isDrawerOpen) {
        facets.classList.add('algolia-facets-drawer--open');
        facets.style.removeProperty('display');
        document.body.classList.add('open-cc', 'algolia-facets-open');
        ensureFacetsBackdrop().classList.add('is-visible');
      }
    }
    pinShowFiltersLabel();
    ensureMobileActiveFacetsSlot();
    enhanceAlgoliaFacetScrollAffordances();
  });

  // Initial pin in case Algolia renders the button before first onRender.
  pinShowFiltersLabel();

  let resizeTimer;
  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      ensureMobileActiveFacetsSlot();
    }, 150);
  });
};

const enhanceAlgoliaFacetScrollAffordances = () => {
  document
    .querySelectorAll(
      '#algolia-shopify-instantsearch .ais-facet-dropdown-container .ais-Panel-body > div:first-child'
    )
    .forEach((scroller) => {
      if (scroller.closest('.ais-facet-price, .ais-facet-slider, .teq-facet--price')) {
        scroller.removeAttribute('data-algolia-scrollable');
        scroller.removeAttribute('data-algolia-scroll-end');
        return;
      }

      const update = () => {
        const overflows = scroller.scrollHeight > scroller.clientHeight + 2;
        scroller.dataset.algoliaScrollable = overflows ? 'true' : 'false';
        const atEnd =
          !overflows ||
          scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 2;
        scroller.dataset.algoliaScrollEnd = atEnd ? 'true' : 'false';
      };

      if (!scroller.dataset.algoliaScrollBound) {
        scroller.dataset.algoliaScrollBound = '1';
        scroller.addEventListener('scroll', update, { passive: true });
      }
      update();
    });
};

const setupAlgoliaSwatchBackReset = () => {
  window.addEventListener('pageshow', () => {
    document
      .querySelectorAll('#algolia-shopify-instantsearch product-card[data-source-file="algolia-instant-search"]')
      .forEach((productCard) => {
        delete productCard.dataset.selectedSwatchHref;
        if (typeof productCard.setDefaultActiveSwatch === 'function') {
          productCard.setDefaultActiveSwatch({ preferPersisted: false });
        }
      });
  });
};

const setupAlgoliaCustomSwatchRenderer = () => {
  document.addEventListener('algolia.instantSearch.onRender', () => {
    if (algoliaSwatchTimer) {
      clearTimeout(algoliaSwatchTimer);
    }

    algoliaSwatchTimer = setTimeout(() => {
      const collectionContainer = document.querySelector('#algolia-shopify-instantsearch');
      if (!collectionContainer) {
        return;
      }

      collectionContainer
        .querySelectorAll('product-card[data-source-file="algolia-instant-search"]')
        .forEach((productCard) => {
          productCard.classList.remove('hide-price');
          bindAlgoliaProductCardSwatches(productCard);

          const primaryImage = productCard.querySelector('.product-primary-image');
          if (primaryImage && window.lazySizes?.loader) {
            window.lazySizes.loader.unveil(primaryImage);
          }
        });

      setupAlgoliaProductCardSecondaryImages();
    }, 50);
  });
};

const ALGOLIA_SEARCH_PAGE_TITLE = 'SEARCH RESULTS';

const setupAlgoliaSearchPageTitle = () => {
  if (!document.querySelector('#main-content[data-template="search"]')) {
    return;
  }

  const applySearchTitle = () => {
    if (window.algoliaShopify?.translations) {
      window.algoliaShopify.translations.searchTitle = ALGOLIA_SEARCH_PAGE_TITLE;
    }

    document
      .querySelectorAll('#algolia-shopify-instantsearch .ais-h2')
      .forEach((heading) => {
        if (heading.textContent !== ALGOLIA_SEARCH_PAGE_TITLE) {
          heading.textContent = ALGOLIA_SEARCH_PAGE_TITLE;
        }
      });
  };

  document.addEventListener('algolia.instantsearch.beforeInit', applySearchTitle, { once: true });
  document.addEventListener('algolia.instantSearch.onRender', applySearchTitle);

  algoliaShopify.hooks.registerHook('beforeInstantSearchMainTemplate', (defaultTemplate) => {
    applySearchTitle();
    return defaultTemplate;
  });
};

(function () {
  window.NatoriAlgoliaPLP = window.NatoriAlgoliaPLP || {};
  window.NatoriAlgoliaPLP.applyFilteredVariantLinkForSwatch =
    applyAlgoliaFilteredVariantLinkForSwatch;

  document.addEventListener('algolia.hooks.initialize', function () {
    try {
      setupFacetClassEnhancer();
      setupMobileGridView();
    } catch (e) {
      console.error('TEQ facet class enhancer failed', e);
    }
    try {
      setupTaxifyRescan();
    } catch (error) {
      console.log('Taxify custom hook rescanAllElements error', error);
    }
    try {
      setupAlgoliaCustomSwatchRenderer();
    } catch (error) {
      console.error('Algolia custom swatch renderer setup failed', error);
    }
    try {
      setupAlgoliaHitsReadyGate();
    } catch (error) {
      console.error('Algolia hits ready gate setup failed', error);
    }
    try {
      setupAlgoliaColorFacetSwatches();
    } catch (error) {
      console.error('Algolia color facet swatches setup failed', error);
    }
    try {
      setupAlgoliaMobileFilterDrawer();
    } catch (error) {
      console.error('Algolia mobile filter drawer setup failed', error);
    }
    try {
      setupAlgoliaSwatchBackReset();
    } catch (error) {
      console.error('Algolia swatch back reset setup failed', error);
    }
    try {
      setupAlgoliaFilteredVariantLinks();
    } catch (error) {
      console.error('Algolia filtered variant links setup failed', error);
    }
    try {
      setupAlgoliaMarketPriceHydration();
    } catch (error) {
      console.error('Algolia market price hydration setup failed', error);
    }
    try {
      setupAlgoliaFacetParamsOptions();
    } catch (error) {
      console.error('Algolia facet params setup failed', error);
    }
    try {
      setupAlgoliaVariantFacetDistinctCounts();
    } catch (error) {
      console.error('Algolia variant facet distinct counts setup failed', error);
    }
    try {
      setupAlgoliaCurrentRefinementsFormatter();
    } catch (error) {
      console.error('Algolia current refinements formatter setup failed', error);
    }
    try {
      setupAlgoliaPriceFacetInputs();
    } catch (error) {
      console.error('Algolia price facet inputs setup failed', error);
    }
    try {
      setupAlgoliaSortPlacement();
    } catch (error) {
      console.error('Algolia sort placement setup failed', error);
    }
    try {
      setupAlgoliaSearchPageTitle();
    } catch (error) {
      console.error('Algolia search page title setup failed', error);
    }
    algoliaShopify.hooks.registerHook(
      'beforeInstantSearchProductTemplate',
      function (_defaultTemplate, hit, html, components, trackSearchAttribution) {
        var refinementList = getAlgoliaActiveRefinementList();
        var requiredVariantOptions = getAlgoliaRequiredVariantOptions(hit, refinementList);
        var productUrl = algoliaShopify.helpers.instantsearchLink(hit);
        if (
          requiredVariantOptions.length &&
          hitSatisfiesAlgoliaRequiredOptions(hit, requiredVariantOptions)
        ) {
          productUrl = buildAlgoliaProductVariantUrl(hit.handle, hit.objectID);
        }
        var hitColorValue = getAlgoliaHitColorValue(hit);
        var fullTitle = algoliaShopify.helpers.fullTitle(
          hit.title,
          hit._distinct,
          hit.variant_title
        );
        var imageUrl = getAlgoliaHitImageUrl(hit);
        var responsiveImage = buildAlgoliaResponsiveImageAttrs(imageUrl);
        var productId = hit.id;
        var styleNo = hit?.meta?.custom?.merch_style_number || '';
        var displayPrice = teqDisplayPriceFromHit(hit, hit._distinct);
        var compareAtRaw = resolveAlgoliaHitCompareAt(hit);
        var priceCents = teqNormalizePriceToCents(hit.price);
        var compareCents = teqNormalizePriceToCents(compareAtRaw);
        var hasPriceRange =
          hit._distinct &&
          hit.variants_min_price != null &&
          hit.variants_max_price != null &&
          hit.variants_min_price !== hit.variants_max_price;
        var showCompare =
          !hasPriceRange &&
          compareCents != null &&
          priceCents != null &&
          compareCents > priceCents;
        // Prefer theme money formatting so strikethrough matches the sale price style.
        var strikedPrice = showCompare
          ? teqFormatPriceFromRaw(compareAtRaw) ||
            algoliaShopify.helpers.displayStrikedPrice(hit.price, compareAtRaw)
          : '';
        var comparePriceContainer = showCompare && strikedPrice
          ? html`<del><span class="amount">${strikedPrice}</span></del>`
          : null;
        var priceContainer = html`<ins><span class="amount">${displayPrice}</span></ins>`;
        var ratingsTemplate = buildAlgoliaReviewsTemplate(hit, html);
        var swatchTemplates = buildAlgoliaSwatchTemplatesFromHit(hit, productUrl, imageUrl, html);
        return html`
          <product-card
            class="product-card text-center ais-hit hide-price"
            data-source-file="algolia-instant-search"
            data-algolia-index="${hit.index}"
            data-algolia-position="${hit.productPosition}"
            data-algolia-queryid="${hit.queryID}"
            data-algolia-objectid="${hit.objectID}"
            data-handle="${hit.handle}"
            data-variant-id="${hit.objectID}"
            data-hit-color="${hitColorValue}"
            data-distinct="${hit._distinct}"
            data-product-id="${productId}"
            data-product-name="${hit.title}"
            data-style-no="${styleNo}"
            data-merch-style-number="${styleNo}"
            data-default-price="${hit.price != null ? hit.price : ''}"
            data-default-compare-at-price="${compareAtRaw != null ? compareAtRaw : ''}"
            onClick="${() => trackSearchAttribution(hit)}"
          >
            <figure class="product-featured-image spin_overlay">
                <a href="${productUrl}" title="${fullTitle}" class="product-featured-image-link aspect-ratio aspect-ratio--adapt" style="--padding-bottom: 150%;">
                    <img
                      class="product-primary-image lazyload"
                      src="${responsiveImage.src}"
                      data-srcset="${responsiveImage.srcset}"
                      data-sizes="auto"
                      sizes="${responsiveImage.sizes}"
                      alt="${fullTitle}"
                      style="width:100%;height:100%;"
                    />
                </a>
            </figure>
            <div class="product-card-info">
                <div class="productInfosection">
                    <div class="productInfoleft">
                        <div class="product-card-vendor">${hit.vendor}</div>
                        <a href="${productUrl}" title="${fullTitle}" class="product-card-title">${components.Highlight({ attribute: 'title', hit })}
                    ${algoliaShopify.helpers.variantTitleAddition(
                      hit,
                      hit._distinct
                    )}</a>
                    </div>
                    <div class="productInfoRight">
                        ${ratingsTemplate}
                        <span class="price${hasPriceRange ? ' has-price-range' : ''}">${comparePriceContainer || ''}${priceContainer}</span>
                    </div>
                </div>
                <div
                  class="product-card-swatch"
                  data-product-id="${productId}"
                  data-product-name="${hit.title}"
                  data-style-no="${styleNo}"
                >
                    <div class="product-card-swatches no-js-hidden" id="swatch_section_${productId}">
                      ${swatchTemplates}
                    </div>
                </div>
            </div>
          </product-card>
        `;
      }
    );
  });
})();
